import React, { useEffect, useMemo, useState, useRef, Fragment } from 'react';
import { Menu, Plus, Search, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, X, Trash2, Calendar, User, Mail, Phone, Users, Briefcase, DollarSign, SlidersHorizontal, MapPin, Clock, AlertTriangle, CheckCircle2, PauseCircle, Loader2, FileText, ClipboardCheck, ShoppingCart, Paperclip, StickyNote, Check, Upload, ShieldCheck, Receipt, Tag, ExternalLink, PoundSterling, Car, TrendingUp, TrendingDown, History, Download } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { downloadCsv } from '../data/csv';
import { Sidebar } from '../components/Sidebar';
import { useSettingsStore } from '../data/settingsStore';
import { useScreenInit } from '../useScreenInit';
// --- Types & Mock Data ---
type JobStatus = 'quote' | 'pcc_in_progress' | 'ongoing' | 'closed' | 'lost';
// Forward workflow order (Lost sits outside the linear flow)
const STATUS_FLOW: JobStatus[] = ['quote', 'pcc_in_progress', 'ongoing', 'closed'];
type CreditCheckStatus = 'not_started' | 'pending' | 'passed' | 'failed';
type PccChecklistItem = {
  id: string;
  label: string;
  done: boolean;
};
type PccInfo = {
  checklist: PccChecklistItem[];
  creditCheckStatus: CreditCheckStatus;
  creditCheckRef?: string;
  creditCheckScore?: string;
  creditCheckDate?: string;
  creditCheckNotes?: string;
  approvedBy?: string;
  approvedAt?: string;
  complete: boolean;
};
type PurchaseOrder = {
  id: string;
  number: string;
  description?: string;
  amount: number;
  date: string;
  fileName?: string;
  fileUrl?: string;
};
type JobNote = {
  id: string;
  body: string;
  author: string;
  createdAt: string;
};
type Attachment = {
  id: string;
  name: string;
  size: string;
  uploadedAt: string;
};
type TimesheetEntry = {
  id: string;
  person: string;
  date: string;
  hours: number;
  department: string;
  hourlyCost?: number;
  notes?: string;
};
type MileageEntry = {
  id: string;
  person: string;
  date: string;
  miles: number;
  department: string;
  notes?: string;
};
const EXPENSE_CATEGORIES = ['Travel', 'Accommodation', 'Meals', 'Materials', 'Equipment', 'Subcontractor', 'Software', 'Other'] as const;
type ExpenseCategory = (typeof EXPENSE_CATEGORIES)[number];
type Expense = {
  id: string;
  value: number;
  category: ExpenseCategory;
  department: string;
  description?: string;
  receiptName: string;
  receiptUrl: string;
  addedBy: string;
  date: string;
};
const INVOICE_CATEGORIES = ['Interim', 'Final', 'Credit note', 'Other'] as const;
export type InvoiceCategory = (typeof INVOICE_CATEGORIES)[number];
export type InvoicePaymentStatus = 'paid' | 'unpaid' | 'cancelled';
export type Invoice = {
  id: string;
  value: number;
  category: InvoiceCategory;
  department: string;
  paymentStatus: InvoicePaymentStatus;
  sageId?: string;
  description?: string;
  fileName?: string;
  fileUrl?: string;
  addedBy: string;
  date: string;
};
// Rates used to value the financial roll-up
const MILEAGE_RATE = 0.45; // £ per mile
const LEGACY_HOURLY_COST = 65; // Used only for historic entries without a stored rate
type JobActivity = {
  id: string;
  action: string;
  actor: string;
  occurredAt: string;
  detail?: string;
};
type Job = {
  id: string;
  ref: string;
  name: string;
  client: string;
  departments: string[];
  status: JobStatus;
  value: number;
  projectDirector: string;
  projectManager: string;
  startDate: string;
  endDate?: string;
  dateCreated: string;
  assignedTo?: string;
  description?: string;
  progress?: number;
  departmentBudgets?: Record<string, Record<string, number>>;
  validatedDepartmentBudgets?: Record<string, Record<string, boolean>>;
  // Extended job-management fields
  likelihood?: number;
  poRequired?: boolean;
  pcc?: PccInfo;
  purchaseOrders?: PurchaseOrder[];
  notes?: JobNote[];
  attachments?: Attachment[];
  timesheets?: TimesheetEntry[];
  expenses?: Expense[];
  invoices?: Invoice[];
  mileageEntries?: MileageEntry[];
  activity?: JobActivity[];
  // Financials roll-up inputs
  invoiced?: number; // total amount invoiced to the client
  // Job address
  street?: string;
  line2?: string;
  city?: string;
  county?: string;
  postcode?: string;
  // Referral
  fromReferral?: boolean;
  referralMethod?: string;
  referringClient?: string;
  // Parent relationship
  parentJobId?: string;
  parentJobRef?: string;
};
const REFERRAL_METHODS = ['Referred by client', 'Existing client', 'Word of mouth', 'Professional network', 'Repeat business', 'Email / newsletter', 'Website enquiry', 'Event / conference', 'Other'] as const;
const DEFAULT_PCC_CHECKLIST: {
  id: string;
  label: string;
}[] = [{
  id: 'engagement',
  label: 'Engagement letter signed'
}, {
  id: 'scope',
  label: 'Scope of work confirmed'
}, {
  id: 'fee',
  label: 'Fee agreement in place'
}, {
  id: 'conflict',
  label: 'Conflict of interest check'
}, {
  id: 'aml',
  label: 'Anti-money-laundering (AML) check'
}, {
  id: 'credit',
  label: 'Client credit check cleared'
}];
const makePcc = (overrides?: Partial<PccInfo>): PccInfo => ({
  checklist: DEFAULT_PCC_CHECKLIST.map(c => ({
    ...c,
    done: false
  })),
  creditCheckStatus: 'not_started',
  complete: false,
  ...overrides
});
const DEPARTMENTS = ['Project Management', 'Surveying', 'Design', 'Cost Consultancy', 'Building Surveying', 'Finance'] as const;
const DEFAULT_JOB_DEPARTMENTS = ['Project Management', 'Design'];
const INITIAL_JOB_DEPARTMENTS: Record<string, string[]> = {
  '1': ['Project Management', 'Surveying', 'Design'],
  '2': ['Project Management', 'Design', 'Cost Consultancy'],
  '3': ['Project Management', 'Building Surveying', 'Design'],
  '4': ['Project Management', 'Surveying', 'Cost Consultancy'],
  '5': ['Project Management', 'Design', 'Building Surveying'],
  '6': ['Project Management', 'Surveying', 'Design'],
  '7': ['Project Management', 'Building Surveying'],
  '8': ['Project Management', 'Surveying', 'Cost Consultancy'],
  '9': ['Project Management', 'Design'],
  '10': ['Project Management', 'Cost Consultancy', 'Finance']
};
export const formatActivityCurrency = (value: number) => new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  maximumFractionDigits: 0
}).format(value);
function activityDateAfter(dateString: string, daysAfter: number) {
  const date = new Date(dateString);
  date.setDate(date.getDate() + daysAfter);
  return date.toISOString();
}
function createInitialJobActivity(job: {
  id: string;
  dateCreated: string;
  projectManager: string;
  status: string;
  notes?: JobNote[];
  invoices?: Invoice[];
  expenses?: Expense[];
  mileageEntries?: MileageEntry[];
  timesheets?: TimesheetEntry[];
  purchaseOrders?: PurchaseOrder[];
}): JobActivity[] {
  const actor = job.projectManager || 'System';
  const statusActivity: JobActivity[] = job.status === 'quote' ? [] : job.status === 'lost' ? [{
    id: `activity-status-lost-${job.id}`,
    action: 'Job changed status',
    actor,
    occurredAt: activityDateAfter(job.dateCreated, 3),
    detail: 'Quote → Lost'
  }] : [{
    id: `activity-status-pcc-${job.id}`,
    action: 'Job changed status',
    actor,
    occurredAt: activityDateAfter(job.dateCreated, 2),
    detail: 'Quote → PCC in progress'
  }, ...(job.status === 'pcc_in_progress' ? [] : [{
    id: `activity-pcc-complete-${job.id}`,
    action: 'PCC marked complete',
    actor,
    occurredAt: activityDateAfter(job.dateCreated, 5)
  }, {
    id: `activity-status-ongoing-${job.id}`,
    action: 'Job changed status',
    actor,
    occurredAt: activityDateAfter(job.dateCreated, 6),
    detail: 'PCC in progress → Ongoing'
  }]), ...(job.status === 'closed' ? [{
    id: `activity-status-closed-${job.id}`,
    action: 'Job changed status',
    actor,
    occurredAt: activityDateAfter(job.dateCreated, 45),
    detail: 'Ongoing → Closed'
  }] : [])];
  const activity: JobActivity[] = [{
    id: `activity-created-${job.id}`,
    action: 'Job was created',
    actor,
    occurredAt: job.dateCreated
  }, ...statusActivity, ...(job.notes || []).map(note => ({
    id: `activity-note-${note.id}`,
    action: 'Note added',
    actor: note.author,
    occurredAt: note.createdAt,
    detail: note.body
  })), ...(job.purchaseOrders || []).map(purchaseOrder => ({
    id: `activity-po-${purchaseOrder.id}`,
    action: 'Purchase order added',
    actor,
    occurredAt: purchaseOrder.date,
    detail: `${purchaseOrder.number} · ${formatActivityCurrency(purchaseOrder.amount)}`
  })), ...(job.invoices || []).map(invoice => ({
    id: `activity-invoice-${invoice.id}`,
    action: 'Job invoice was raised',
    actor: invoice.addedBy,
    occurredAt: invoice.date,
    detail: `${invoice.category} · ${formatActivityCurrency(invoice.value)}`
  })), ...(job.expenses || []).map(expense => ({
    id: `activity-expense-${expense.id}`,
    action: 'Expense added',
    actor: expense.addedBy,
    occurredAt: expense.date,
    detail: `${expense.category} · ${formatActivityCurrency(expense.value)}`
  })), ...(job.mileageEntries || []).map(entry => ({
    id: `activity-mileage-${entry.id}`,
    action: 'Mileage added',
    actor: entry.person,
    occurredAt: entry.date,
    detail: `${entry.miles.toLocaleString('en-GB')} mi`
  })), ...(job.timesheets || []).map(entry => ({
    id: `activity-time-${entry.id}`,
    action: 'Time logged',
    actor: entry.person,
    occurredAt: entry.date,
    detail: `${entry.hours}h`
  }))];
  return activity.sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime());
}
export const INITIAL_JOBS = [{
  id: '1',
  ref: 'JOB-1041',
  name: 'Midtown Tower Refurb',
  client: 'Atlas Property Holdings',
  status: 'ongoing',
  value: 142000,
  projectDirector: 'Eleanor Hughes',
  projectManager: 'Sarah Mitchell',
  startDate: '2026-03-01',
  dateCreated: '2026-02-15',
  assignedTo: 'Sarah Mitchell',
  description: 'Complete structural assessment and renovation planning for Midtown commercial tower.',
  progress: 65,
  poRequired: true,
  pcc: {
    checklist: DEFAULT_PCC_CHECKLIST.map(c => ({
      ...c,
      done: true
    })),
    creditCheckStatus: 'passed',
    creditCheckRef: 'CC-20451',
    creditCheckScore: '92',
    creditCheckDate: '2026-02-18',
    creditCheckNotes: 'Experian check. No adverse history.',
    approvedBy: 'Eleanor Hughes',
    approvedAt: '2026-02-20T10:00:00Z',
    complete: true
  },
  purchaseOrders: [{
    id: 'po1',
    number: 'PO-88231',
    description: 'Phase 1 structural survey',
    amount: 60000,
    date: '2026-02-22'
  }, {
    id: 'po2',
    number: 'PO-88247',
    description: 'Renovation planning package',
    amount: 82000,
    date: '2026-03-05'
  }],
  notes: [{
    id: 'jn1',
    body: 'Client confirmed phase 2 will follow on directly. Keep the survey team on standby.',
    author: 'Sarah Mitchell',
    createdAt: '2026-03-04T14:10:00Z'
  }],
  attachments: [{
    id: 'at1',
    name: 'Engagement-Letter-signed.pdf',
    size: '248 KB',
    uploadedAt: '2026-02-18T09:30:00Z'
  }, {
    id: 'at2',
    name: 'Structural-survey-scope.docx',
    size: '64 KB',
    uploadedAt: '2026-02-19T16:05:00Z'
  }],
  timesheets: [{
    id: 'ts1',
    person: 'Sarah Mitchell',
    date: '2026-03-02',
    hours: 7.5,
    notes: 'Site visit + survey prep'
  }, {
    id: 'ts2',
    person: 'Priya Patel',
    date: '2026-03-03',
    hours: 4,
    notes: 'Drawings review'
  }, {
    id: 'ts3',
    person: 'Sarah Mitchell',
    date: '2026-03-06',
    hours: 6
  }],
  expenses: [{
    id: 'ex1',
    value: 142.5,
    category: 'Travel',
    description: 'Rail fare for Midtown site visit',
    receiptName: 'rail-receipt.pdf',
    receiptUrl: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=800&q=80',
    addedBy: 'Sarah Mitchell',
    date: '2026-03-02T18:20:00Z'
  }, {
    id: 'ex2',
    value: 38.0,
    category: 'Meals',
    receiptName: 'lunch.jpg',
    receiptUrl: 'https://images.unsplash.com/photo-1572441710534-3c2b1f0a2e3a?auto=format&fit=crop&w=800&q=80',
    addedBy: 'Matthew Carter',
    date: '2026-03-03T13:05:00Z'
  }],
  invoices: [{
    id: 'inv1',
    value: 60000,
    category: 'Interim',
    department: 'Structural',
    paymentStatus: 'paid',
    description: 'Phase 1 structural survey — interim invoice',
    fileName: 'INV-20451.pdf',
    fileUrl: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=800&q=80',
    addedBy: 'Matthew Carter',
    date: '2026-03-10T09:30:00Z'
  }, {
    id: 'inv2',
    value: 18000,
    category: 'Interim',
    department: 'Planning',
    paymentStatus: 'unpaid',
    addedBy: 'Sarah Mitchell',
    date: '2026-04-18T09:30:00Z'
  }, {
    id: 'inv3',
    value: -2500,
    category: 'Credit note',
    department: 'Structural',
    paymentStatus: 'paid',
    addedBy: 'Matthew Carter',
    date: '2026-04-26T09:30:00Z'
  }, {
    id: 'inv4',
    value: 7500,
    category: 'Other',
    department: 'Planning',
    paymentStatus: 'cancelled',
    addedBy: 'Sarah Mitchell',
    date: '2026-05-02T09:30:00Z'
  }],
  mileageEntries: [{
    id: 'mil1',
    person: 'Sarah Mitchell',
    date: '2026-03-02',
    miles: 220,
    notes: 'Return trip to Midtown site'
  }, {
    id: 'mil2',
    person: 'Priya Patel',
    date: '2026-03-05',
    miles: 100
  }],
  invoiced: 60000
}, {
  id: '2',
  ref: 'JOB-1055',
  name: 'Sunset Heights Phase II',
  client: 'Crestview Developments',
  status: 'ongoing',
  value: 215000,
  projectDirector: 'Marcus Bell',
  projectManager: 'Michael Chen',
  startDate: '2026-01-15',
  dateCreated: '2025-12-20',
  assignedTo: 'Michael Chen',
  description: 'Phase II development assessment for residential complex.',
  progress: 42
}, {
  id: '3',
  ref: 'JOB-1048',
  name: 'Magnificent Mile Tower',
  client: 'Vanguard Properties',
  status: 'pcc_in_progress',
  value: 198000,
  projectDirector: 'Eleanor Hughes',
  projectManager: 'David Reyes',
  startDate: '2026-02-01',
  dateCreated: '2026-01-10',
  assignedTo: 'David Reyes',
  progress: 78
}, {
  id: '4',
  ref: 'JOB-1029',
  name: 'Brooklyn Mixed-Use Survey',
  client: 'Atlas Property Holdings',
  status: 'quote',
  value: 88500,
  projectDirector: 'Marcus Bell',
  projectManager: 'Priya Patel',
  referralMethod: 'Referred by client',
  referringClient: 'Atlas Property Holdings',
  startDate: '2026-04-12',
  dateCreated: '2026-03-28',
  assignedTo: 'Priya Patel',
  progress: 35
}, {
  id: '5',
  ref: 'JOB-1020',
  name: 'Venice Boardwalk Retail',
  client: 'Crestview Developments',
  status: 'ongoing',
  value: 178000,
  projectDirector: 'Eleanor Hughes',
  projectManager: 'Karen Sloane',
  startDate: '2026-03-04',
  dateCreated: '2026-02-18',
  progress: 52
}, {
  id: '6',
  ref: 'JOB-1012',
  name: 'River North Lofts',
  client: 'Beacon Real Estate',
  status: 'pcc_in_progress',
  value: 96500,
  projectDirector: 'Marcus Bell',
  projectManager: 'Lena Ortiz',
  startDate: '2026-02-18',
  dateCreated: '2026-01-30',
  progress: 88
}, {
  id: '7',
  ref: 'JOB-0987',
  name: 'Lexington Ave Façade',
  client: 'Atlas Property Holdings',
  status: 'closed',
  value: 64200,
  projectDirector: 'Eleanor Hughes',
  projectManager: 'Sarah Mitchell',
  startDate: '2025-09-08',
  endDate: '2026-01-22',
  dateCreated: '2025-08-15',
  assignedTo: 'Sarah Mitchell',
  progress: 100
}, {
  id: '8',
  ref: 'JOB-0978',
  name: 'Lincoln Park Condos',
  client: 'Beacon Real Estate',
  status: 'closed',
  value: 124000,
  projectDirector: 'Marcus Bell',
  projectManager: 'Karen Sloane',
  startDate: '2025-08-14',
  endDate: '2026-04-10',
  dateCreated: '2025-07-22',
  progress: 100
}, {
  id: '9',
  ref: 'JOB-0995',
  name: 'Wicker Park Lofts',
  client: 'Vanguard Properties',
  status: 'lost',
  value: 72000,
  projectDirector: 'Eleanor Hughes',
  projectManager: 'David Reyes',
  startDate: '2025-10-15',
  dateCreated: '2025-09-28',
  assignedTo: 'David Reyes',
  progress: 25
}, {
  id: '10',
  ref: 'JOB-0951',
  name: 'Hudson Yards Fit-out',
  client: 'Atlas Property Holdings',
  status: 'quote',
  value: 41000,
  projectDirector: 'Marcus Bell',
  projectManager: 'Priya Patel',
  startDate: '2025-11-02',
  dateCreated: '2025-10-12',
  progress: 18
}].map(job => {
  const departments = INITIAL_JOB_DEPARTMENTS[job.id] || DEFAULT_JOB_DEPARTMENTS;
  return {
    ...job,
    departments,
    timesheets: job.timesheets?.map(entry => ({
      ...entry,
      department: departments[0]
    })),
    expenses: job.expenses?.map(entry => ({
      ...entry,
      department: departments[0]
    })),
    mileageEntries: job.mileageEntries?.map(entry => ({
      ...entry,
      department: departments[0]
    })),
    invoices: job.invoices?.map(entry => ({
      ...entry
    })),
    activity: createInitialJobActivity(job)
  };
}) as unknown as Job[];
// Current signed-in user (would come from auth context in a real app)
const CURRENT_USER = 'Matthew Carter';
type SortField = 'ref' | 'name' | 'client' | 'departments' | 'projectDirector' | 'projectManager' | 'dateCreated' | 'value' | 'invoiced' | 'netProfit' | 'startDate' | 'status';
type SortOrder = 'asc' | 'desc';
const formatCurrency = (value: number) => new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  maximumFractionDigits: 0
}).format(value);
function getTimesheetCost(entry: TimesheetEntry) {
  return entry.hours * (entry.hourlyCost ?? LEGACY_HOURLY_COST);
}
function getTotalInvoiced(job: Job) {
  return (job.invoices || []).filter(invoice => invoice.paymentStatus !== 'cancelled').reduce((sum, invoice) => sum + invoice.value, 0);
}
function calculateNetProfit(job: Job) {
  const invoiced = getTotalInvoiced(job);
  const expenses = (job.expenses || []).reduce((sum, expense) => sum + expense.value, 0);
  const mileage = (job.mileageEntries || []).reduce((sum, entry) => sum + entry.miles, 0) * MILEAGE_RATE;
  const time = (job.timesheets || []).reduce((sum, entry) => sum + getTimesheetCost(entry), 0);
  return invoiced - expenses - mileage - time;
}
function getNextChildJobRef(parentRef: string, jobs: Job[]) {
  const existingRefs = new Set(jobs.map(job => job.ref));
  for (let index = 0; index < 702; index += 1) {
    let value = index;
    let suffix = '';
    do {
      suffix = String.fromCharCode(65 + value % 26) + suffix;
      value = Math.floor(value / 26) - 1;
    } while (value >= 0);
    const candidate = `${parentRef}-${suffix}`;
    if (!existingRefs.has(candidate)) return candidate;
  }
  return `${parentRef}-${Date.now()}`;
}
const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];
export function Jobs() {
  const screenInit = useScreenInit();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'jobs' | 'budget'>(screenInit?.activeTab === 'budget' ? 'budget' : 'jobs');
  const [jobs, setJobs] = useState<Job[]>(INITIAL_JOBS);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterStatus, setFilterStatus] = useState<JobStatus | 'all'>('all');
  const [showValidatedOnly, setShowValidatedOnly] = useState(false);
  const [sortField, setSortField] = useState<SortField>('ref');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isAdmin] = useState(true);
  // KPI Totals
  const totals = useMemo(() => ({
    total: jobs.length,
    active: jobs.filter(j => j.status === 'ongoing').length,
    revenue: jobs.reduce((sum, j) => sum + j.value, 0)
  }), [jobs]);
  const filteredAndSortedJobs = useMemo(() => {
    let result = [...jobs];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(j => j.name.toLowerCase().includes(q) || j.ref.toLowerCase().includes(q) || j.client.toLowerCase().includes(q) || j.projectDirector.toLowerCase().includes(q) || j.projectManager.toLowerCase().includes(q));
    }
    if (filterDepartment) {
      result = result.filter(j => j.departments.includes(filterDepartment));
    }
    if (filterStatus !== 'all') {
      result = result.filter(j => j.status === filterStatus);
    }
    result.sort((a, b) => {
      let comparison = 0;
      if (sortField === 'ref') comparison = a.ref.localeCompare(b.ref, undefined, {
        numeric: true
      });else if (sortField === 'name') comparison = a.name.localeCompare(b.name);else if (sortField === 'client') comparison = a.client.localeCompare(b.client);else if (sortField === 'departments') comparison = a.departments.join(', ').localeCompare(b.departments.join(', '));else if (sortField === 'projectDirector') comparison = a.projectDirector.localeCompare(b.projectDirector);else if (sortField === 'projectManager') comparison = a.projectManager.localeCompare(b.projectManager);else if (sortField === 'dateCreated') comparison = new Date(a.dateCreated).getTime() - new Date(b.dateCreated).getTime();else if (sortField === 'value') comparison = a.value - b.value;else if (sortField === 'invoiced') comparison = getTotalInvoiced(a) - getTotalInvoiced(b);else if (sortField === 'netProfit') comparison = calculateNetProfit(a) - calculateNetProfit(b);else if (sortField === 'startDate') comparison = new Date(a.startDate).getTime() - new Date(b.startDate).getTime();else if (sortField === 'status') comparison = a.status.localeCompare(b.status);
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    return result;
  }, [jobs, searchQuery, filterDepartment, filterStatus, sortField, sortOrder]);
  useEffect(() => {
    setPage(1);
  }, [searchQuery, filterDepartment, filterStatus, pageSize]);
  const totalPages = Math.max(1, Math.ceil(filteredAndSortedJobs.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * pageSize;
  const paginatedJobs = filteredAndSortedJobs.slice(pageStart, pageStart + pageSize);
  const showingFrom = filteredAndSortedJobs.length === 0 ? 0 : pageStart + 1;
  const showingTo = Math.min(pageStart + pageSize, filteredAndSortedJobs.length);
  const exportJobs = () => {
    downloadCsv('jobs-export.csv', [{
      label: 'Job ID',
      value: job => job.ref
    }, {
      label: 'Job title',
      value: job => job.name
    }, {
      label: 'Client',
      value: job => job.client
    }, {
      label: 'Departments',
      value: job => job.departments.join('; ')
    }, {
      label: 'Project director',
      value: job => job.projectDirector
    }, {
      label: 'Project manager',
      value: job => job.projectManager
    }, {
      label: 'Budget',
      value: job => job.value
    }, {
      label: 'Invoiced',
      value: job => getTotalInvoiced(job)
    }, {
      label: 'Net profit',
      value: job => calculateNetProfit(job)
    }, {
      label: 'Status',
      value: job => STATUS_CONFIG[job.status].label
    }, {
      label: 'Start date',
      value: job => job.startDate
    }, {
      label: 'Date created',
      value: job => job.dateCreated
    }], filteredAndSortedJobs);
  };
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };
  const openJobDrawer = (job: Job) => {
    setSelectedJob(job);
    setIsDrawerOpen(true);
  };
  const openNewJobDrawer = () => {
    setSelectedJob(null);
    setIsDrawerOpen(true);
  };
  const handleSaveJob = (jobData: Partial<Job>) => {
    if (selectedJob) {
      setJobs(jobs.map(j => j.id === selectedJob.id ? {
        ...j,
        ...jobData
      } as Job : j));
    } else {
      const parentJob = jobs.find(job => job.id === jobData.parentJobId);
      const newJob: Job = {
        ...jobData,
        parentJobId: parentJob?.id,
        parentJobRef: parentJob?.ref,
        id: Math.random().toString(36).substr(2, 9),
        ref: parentJob ? getNextChildJobRef(parentJob.ref, jobs) : `JOB-${String(1000 + jobs.length + 1)}`,
        dateCreated: new Date().toISOString().split('T')[0]
      } as Job;
      setJobs([newJob, ...jobs]);
    }
    setIsDrawerOpen(false);
  };
  // Auto-save: persist edits to an existing job without closing the drawer
  const handleAutoSaveJob = (jobData: Partial<Job>) => {
    if (!selectedJob) return;
    setJobs(prev => prev.map(j => j.id === selectedJob.id ? {
      ...j,
      ...jobData
    } as Job : j));
  };
  const handleDeleteJob = (id: string) => {
    setJobs(jobs.filter(j => j.id !== id));
    setIsDrawerOpen(false);
  };
  return <div className="flex h-screen w-screen overflow-hidden bg-[#1F5C9E] font-sans" data-id="element-1180" data-mp="3907pk7n5rufa">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} data-id="element-1181" data-mp="3414sb9df6shv" />

      <main className="flex-1 min-w-0 w-full p-0 md:py-3 md:pr-3 md:pl-0 overflow-hidden flex" data-id="element-1182" data-mp="2z33d4zj47dbo">
        <div className="flex-1 min-w-0 w-full bg-white md:rounded-2xl shadow-xl flex flex-col overflow-x-hidden overflow-y-auto" data-id="element-1183" data-mp="2v0yka30hqkq3">
          {/* Header */}
          <div className="p-5 md:p-8 md:pb-0 border-b border-slate-100" data-id="element-1184" data-mp="2q2x2dleswrg7">
            <button onClick={() => setSidebarOpen(true)} className="md:hidden flex items-center gap-2 text-[#14466B] mb-4 hover:opacity-80 transition-opacity" aria-label="Open menu" data-id="element-1185" data-mp="15j1g3esxea51">
              <Menu className="w-6 h-6" data-id="element-1186" data-mp="1ekj21dt0l61n" />
            </button>

            <div data-id="element-1187" data-mp="1eiya7vy3417h">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-1188" data-mp="1jjlw637ghruu">Jobs</h1>
              <p className="text-slate-500 text-sm mt-1" data-id="element-1189" data-mp="1jhhvdu4ut8ys">
                Manage active projects and assignments across all departments.
              </p>
            </div>

            {/* Tabs + inline controls */}
            <div className="flex flex-col gap-3 mt-6 lg:flex-row lg:items-end lg:justify-between" data-id="element-1190" data-mp="vu9rngn2q7yv">
              <div className="flex items-center gap-6" data-id="element-1191" data-mp="10kzy6x78kgv5">
                <button onClick={() => setActiveTab('jobs')} className={`pb-3 text-sm font-medium transition-colors relative whitespace-nowrap ${activeTab === 'jobs' ? 'text-[#14466B]' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1192" data-mp="10mkp8cucdho9">
                  All Jobs
                  {activeTab === 'jobs' && <motion.div layoutId="jobs-active-tab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#14466B]" data-id="element-1193" data-mp="1bekx7v6cv1ns" />}
                </button>
                <button onClick={() => setActiveTab('budget')} className={`pb-3 text-sm font-medium transition-colors relative whitespace-nowrap ${activeTab === 'budget' ? 'text-[#14466B]' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1194" data-mp="16eoz2ckca4g5">
                  Monthly Budget Allocations
                  {activeTab === 'budget' && <motion.div layoutId="jobs-active-tab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#14466B]" data-id="element-1195" data-mp="nhnkq14warzj" />}
                </button>
              </div>

              {/* Search + Filters — inline with tabs */}
              <div className="flex items-center gap-3 pb-3" data-id="element-1196" data-mp="wi2hpxr81viv">
                <div className="relative flex-1 lg:flex-none lg:w-56" data-id="element-1197" data-mp="wkonbrujy0yg">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-1198" data-mp="2cv0ui5vs1881" />
                  <input type="text" placeholder="Search jobs..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 rounded-lg border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none transition-all text-sm text-slate-700 placeholder:text-slate-400" data-id="element-1199" data-mp="27x9117fhhe1q" />
                </div>

                <FilterPopover filterDepartment={filterDepartment} setFilterDepartment={setFilterDepartment} filterStatus={filterStatus} setFilterStatus={setFilterStatus} showValidatedOnly={showValidatedOnly} setShowValidatedOnly={setShowValidatedOnly} sortField={sortField} sortOrder={sortOrder} onSortChange={(field, order) => {
                setSortField(field);
                setSortOrder(order);
              }} data-id="element-1200" data-mp="2bemzd4g9bkzt" />

                {activeTab === 'jobs' && <button type="button" onClick={exportJobs} className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-100" title="Export filtered jobs as CSV" data-id="element-1201" data-mp="2uc7mo73dibib"><Download className="h-4 w-4" data-id="element-1202" data-mp="1mu6lp5n8u8pc" /><span className="hidden xl:inline" data-id="element-1203" data-mp="1i1dk2qn2nghh">Export CSV</span></button>}
                {activeTab === 'jobs' && <button onClick={openNewJobDrawer} className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[#6A9A24] hover:bg-[#587F1D] text-white text-sm font-medium transition-colors whitespace-nowrap shadow-sm" data-id="element-1204" data-mp="1d1r8elmw68as">
                    <Plus className="w-4 h-4" data-id="element-1205" data-mp="25qexyteobju5" />
                    <span className="hidden sm:inline" data-id="element-1206" data-mp="20rliivfttirc">New job</span>
                  </button>}
              </div>
            </div>
          </div>

          {activeTab === 'jobs' ? <>
              {/* Table Area */}
              <div className="flex-1 flex flex-col px-5 md:px-8 pt-5 md:pt-6 pb-8 md:pb-10 min-h-0" data-id="element-1207" data-mp="1rqdl0fl4wlyb">
                {filteredAndSortedJobs.length === 0 ? <div className="flex-1 flex flex-col items-center justify-center py-16 text-center border-2 border-dashed border-slate-200 rounded-xl bg-slate-50" data-id="element-1208" data-mp="l99xvmj804lf">
                    <Briefcase className="w-12 h-12 text-slate-300 mb-4" data-id="element-1209" data-mp="2s0wrwhc86dph" />
                    <h3 className="text-lg font-medium text-slate-900 mb-1" data-id="element-1210" data-mp="2wy6qonli81pz">
                      No jobs found
                    </h3>
                    <p className="text-slate-500 text-sm max-w-sm" data-id="element-1211" data-mp="30yqo7s9zp7cn">
                      We couldn't find any jobs matching your current filters.
                      Try adjusting your search or adding a new job.
                    </p>
                    <button onClick={() => {
                setSearchQuery('');
                setFilterDepartment('');
                setFilterStatus('all');
              }} className="mt-4 text-[#14466B] text-sm font-medium hover:underline" data-id="element-1212" data-mp="3fp7lr0ji7d49">
                      Clear filters
                    </button>
                  </div> : <>
                    {/* Desktop Table */}
                    <div className="hidden md:block flex-1 min-h-0 overflow-auto border border-slate-200 rounded-xl" data-id="element-1213" data-mp="1qcm9wd9oz6p7">
                      <table className="w-full text-left border-collapse" data-id="element-1214" data-mp="1fxwu8s594kj1">
                        <thead className="sticky top-0 z-10" data-id="element-1215" data-mp="1fvk59v12t9wh">
                          <tr className="bg-slate-50 border-b border-slate-200" data-id="element-1216" data-mp="1kp54fqeeo9x9">
                            <SortableHeader label="Job ID" field="ref" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} className="w-28" data-id="element-1217" data-mp="16xrnmk7vtq5x" />
                            <SortableHeader label="Job Title" field="name" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} data-id="element-1218" data-mp="1aw7gh4qn9sr9" />
                            <SortableHeader label="Departments" field="departments" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} data-id="element-1219" data-mp="23nhd7b1enhsb" />
                            <SortableHeader label="Project Director" field="projectDirector" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} data-id="element-1220" data-mp="1ppkd44nqst8w" />
                            <SortableHeader label="Project Manager" field="projectManager" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} data-id="element-1221" data-mp="e79izerifgqe" />
                            <SortableHeader label="Budget" field="value" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} className="w-32" data-id="element-1222" data-mp="aqdgid9hllzp" />
                            <SortableHeader label="Invoiced" field="invoiced" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} className="w-32" data-id="element-1223" data-mp="6o0dhwm4k26m" />
                            <SortableHeader label="Net profit" field="netProfit" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} className="w-32" data-id="element-1224" data-mp="foyhiewkc9p7" />
                            <SortableHeader label="Status" field="status" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} className="w-44" data-id="element-1225" data-mp="3o375f9o54cb1" />
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100" data-id="element-1226" data-mp="3t4czbatje5m5">
                          {paginatedJobs.map(job => <tr key={job.id} onClick={() => openJobDrawer(job)} className="hover:bg-slate-50 cursor-pointer transition-colors group" data-id="element-1227" data-mp="35esdmk600u96">
                              <td className="py-3 px-4 text-sm text-slate-500 font-medium" data-id="element-1228" data-mp="30ew9mkxax2tm">
                                {job.ref}
                              </td>
                              <td className="py-3 px-4 text-sm text-slate-900 font-medium group-hover:text-[#14466B] transition-colors" data-id="element-1229" data-mp="19b41xh5rndhy">
                                {job.name}
                              </td>
                              <td className="py-3 px-4 text-sm text-slate-600" data-id="element-1230" data-mp="1i2w9nk3eo8iu">
                                <div className="flex flex-wrap gap-1" data-id="element-1231" data-mp="le85nroiylsf">
                                  {job.departments.map(department => <span key={department} className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-700" data-id="element-1232" data-mp="qbzx6kl9c5w3">
                                      {department}
                                    </span>)}
                                </div>
                              </td>
                              <td className="py-3 px-4 text-sm text-slate-600" data-id="element-1233" data-mp="25ta4fiddklh4">
                                {job.projectDirector}
                              </td>
                              <td className="py-3 px-4 text-sm text-slate-600" data-id="element-1234" data-mp="2kvi9uz411cly">
                                {job.projectManager}
                              </td>
                              <td className="py-3 px-4 text-sm text-slate-900 font-semibold tabular-nums" data-id="element-1235" data-mp="2b6i9pcdsgb66">
                                {formatCurrency(job.value)}
                              </td>
                              <td className="py-3 px-4 text-sm font-semibold text-slate-900 tabular-nums" data-id="element-1236" data-mp="225si6kvk96y9">
                                {formatCurrency(getTotalInvoiced(job))}
                              </td>
                              <td className={`py-3 px-4 text-sm font-semibold tabular-nums ${calculateNetProfit(job) >= 0 ? 'text-emerald-700' : 'text-rose-600'}`} data-id="element-1237" data-mp="1s7cruk4cy1pt">
                                {calculateNetProfit(job) < 0 ? `−${formatCurrency(Math.abs(calculateNetProfit(job)))}` : formatCurrency(calculateNetProfit(job))}
                              </td>
                              <td className="py-3 px-4" data-id="element-1238" data-mp="3iihafkxub4p4">
                                <StatusBadge status={job.status} data-id="element-1239" data-mp="3potjhtda0uvb" />
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile Cards */}
                    <div className="md:hidden space-y-3" data-id="element-1240" data-mp="3ko5icevc2xnd">
                      {paginatedJobs.map(job => <div key={job.id} onClick={() => openJobDrawer(job)} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm active:bg-slate-50 transition-colors" data-id="element-1241" data-mp="3kq9x5rurth2f">
                          <div className="flex justify-between items-start mb-3" data-id="element-1242" data-mp="3bptweq5ewo19">
                            <h3 className="font-semibold text-slate-900 text-sm" data-id="element-1243" data-mp="2x0fh71f5ncdy">
                              {job.name}
                            </h3>
                            <span className="text-xs font-medium text-slate-400" data-id="element-1244" data-mp="2nxmtf856lisv">
                              {job.ref}
                            </span>
                          </div>
                          <div className="mb-3 flex items-center gap-2 flex-wrap" data-id="element-1245" data-mp="29b7jwtvz5cs6">
                            <StatusBadge status={job.status} data-id="element-1246" data-mp="29daiw3f3ug91" />
                            {job.departments.map(department => <span key={department} className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-700" data-id="element-1247" data-mp="24eph3zk7foih">
                                {department}
                              </span>)}
                          </div>
                          <div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-100" data-id="element-1248" data-mp="2s88seooqlv0v">
                            <div data-id="element-1249" data-mp="2n94ke1dju3c4">
                              <div className="text-[10px] text-slate-400 uppercase tracking-wide font-medium" data-id="element-1250" data-mp="31zvbf77bgye6">
                                Project Director
                              </div>
                              <div className="text-sm font-medium text-slate-900" data-id="element-1251" data-mp="3b0jqm58g54sn">
                                {job.projectDirector}
                              </div>
                            </div>
                            <div data-id="element-1252" data-mp="23ou4j8e44pyi">
                              <div className="text-[10px] text-slate-400 uppercase tracking-wide font-medium" data-id="element-1253" data-mp="23plmhgesep6b">
                                Project Manager
                              </div>
                              <div className="text-sm font-medium text-slate-900" data-id="element-1254" data-mp="2co7499cfq061">
                                {job.projectManager}
                              </div>
                            </div>
                            <div data-id="element-1255" data-mp="2me0hsfw50pcx">
                              <div className="text-[10px] text-slate-400 uppercase tracking-wide font-medium" data-id="element-1256" data-mp="2mh62zk1nb73d">
                                Budget
                              </div>
                              <div className="text-sm font-semibold text-slate-900" data-id="element-1257" data-mp="2wavcenpai5lx">
                                {formatCurrency(job.value)}
                              </div>
                            </div>
                            <div data-id="element-1258" data-mp="121b8dnfdk2bb">
                              <div className="text-[10px] text-slate-400 uppercase tracking-wide font-medium" data-id="element-1259" data-mp="15zs4lbx9bsdy">
                                Invoiced
                              </div>
                              <div className="text-sm font-semibold text-slate-900" data-id="element-1260" data-mp="tpspu5gbrqu7">
                                {formatCurrency(getTotalInvoiced(job))}
                              </div>
                            </div>
                            <div data-id="element-1261" data-mp="kqnut93z2fms">
                              <div className="text-[10px] text-slate-400 uppercase tracking-wide font-medium" data-id="element-1262" data-mp="knjh9l4ma7jg">
                                Net profit
                              </div>
                              <div className={`text-sm font-semibold ${calculateNetProfit(job) >= 0 ? 'text-emerald-700' : 'text-rose-600'}`} data-id="element-1263" data-mp="1chlgvubu31za">
                                {calculateNetProfit(job) < 0 ? `−${formatCurrency(Math.abs(calculateNetProfit(job)))}` : formatCurrency(calculateNetProfit(job))}
                              </div>
                            </div>
                          </div>
                        </div>)}
                    </div>

                    {/* Pagination */}
                    <Pagination page={safePage} totalPages={totalPages} pageSize={pageSize} setPageSize={setPageSize} setPage={setPage} showingFrom={showingFrom} showingTo={showingTo} total={filteredAndSortedJobs.length} data-id="element-1264" data-mp="1s1wxocxa4y9u" />
                  </>}
              </div>
            </> : <BudgetAllocationsTab jobs={filteredAndSortedJobs} onOpenJob={openJobDrawer} onUpdateBudget={(jobId, department, monthKey, amount) => {
          setJobs(prev => prev.map(j => {
            if (j.id !== jobId) return j;
            return {
              ...j,
              departmentBudgets: {
                ...(j.departmentBudgets || {}),
                [department]: {
                  ...(j.departmentBudgets?.[department] || {}),
                  [monthKey]: amount
                }
              },
              validatedDepartmentBudgets: {
                ...(j.validatedDepartmentBudgets || {}),
                [department]: {
                  ...(j.validatedDepartmentBudgets?.[department] || {}),
                  [monthKey]: false
                }
              }
            };
          }));
        }} onValidateBudget={(jobId, department, monthKey) => {
          setJobs(prev => prev.map(j => j.id === jobId ? {
            ...j,
            validatedDepartmentBudgets: {
              ...(j.validatedDepartmentBudgets || {}),
              [department]: {
                ...(j.validatedDepartmentBudgets?.[department] || {}),
                [monthKey]: true
              }
            }
          } : j));
        }} onUnvalidateBudget={(jobId, department, monthKey) => {
          setJobs(prev => prev.map(j => j.id === jobId ? {
            ...j,
            validatedDepartmentBudgets: {
              ...(j.validatedDepartmentBudgets || {}),
              [department]: {
                ...(j.validatedDepartmentBudgets?.[department] || {}),
                [monthKey]: false
              }
            }
          } : j));
        }} showValidatedOnly={showValidatedOnly} data-id="element-1265" data-mp="1n56h7ohkc0nw" />}
        </div>
      </main>

      <AnimatePresence data-id="element-1266" data-mp="21fg2syqriper">
        {isDrawerOpen && <JobDrawer job={selectedJob} availableJobs={jobs} isAdmin={isAdmin} onClose={() => setIsDrawerOpen(false)} onSave={handleSaveJob} onAutoSave={handleAutoSaveJob} onDelete={selectedJob && isAdmin ? () => handleDeleteJob(selectedJob.id) : undefined} data-id="element-1267" data-mp="1wjhk9rs481m9" />}
      </AnimatePresence>
    </div>;
}
// --- Status Badge ---
const STATUS_CONFIG: Record<JobStatus, {
  label: string;
  bg: string;
  text: string;
  dot: string;
}> = {
  quote: {
    label: 'Quote',
    bg: 'bg-slate-100',
    text: 'text-slate-600',
    dot: 'bg-slate-400'
  },
  pcc_in_progress: {
    label: 'PCC In Progress',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    dot: 'bg-amber-500'
  },
  ongoing: {
    label: 'Ongoing',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    dot: 'bg-emerald-500'
  },
  closed: {
    label: 'Closed',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    dot: 'bg-blue-500'
  },
  lost: {
    label: 'Lost',
    bg: 'bg-rose-50',
    text: 'text-rose-700',
    dot: 'bg-rose-500'
  }
};
function StatusBadge({
  status
}: {
  status: JobStatus;
}) {
  const {
    bg,
    text,
    dot,
    label
  } = STATUS_CONFIG[status];
  return <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${bg} ${text}`} data-id="element-1268" data-mp="2xidrylomg4cx">
      <span className="relative flex w-1.5 h-1.5" aria-hidden data-id="element-1269" data-mp="3p6p3cyeapv2z">
        {status === 'ongoing' && <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" data-id="element-1270" data-mp="3u60hdzna2mgv" />}
        <span className={`relative inline-flex w-1.5 h-1.5 rounded-full ${dot}`} data-id="element-1271" data-mp="3gfopo1ulupq9" />
      </span>
      {label}
    </span>;
}
// Larger, more prominent status indicator used in the job drawer header
function ProminentStatus({
  status
}: {
  status: JobStatus;
}) {
  const {
    bg,
    text,
    dot,
    label
  } = STATUS_CONFIG[status];
  return <span className={`inline-flex items-center gap-2 pl-3 pr-4 py-1.5 rounded-full text-sm font-semibold border ${bg} ${text} border-current/20`} data-id="element-1272" data-mp="gti275d3k3ep">
      <span className="relative flex w-2.5 h-2.5" aria-hidden data-id="element-1273" data-mp="ml90zdxylzj7">
        {status === 'ongoing' && <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" data-id="element-1274" data-mp="hmfs5ytmawpd" />}
        <span className={`relative inline-flex w-2.5 h-2.5 rounded-full ${dot}`} data-id="element-1275" data-mp="coe5l3wzgf95" />
      </span>
      {label}
    </span>;
}
// --- Progress Bar ---
function ProgressBar({
  progress
}: {
  progress: number;
}) {
  return <div className="flex items-center gap-2" data-id="element-1276" data-mp="2x010hq6cp95">
      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-1277" data-mp="3v1lr8m7set37">
        <div className="h-full bg-[#14466B] rounded-full transition-all duration-300" style={{
        width: `${progress}%`
      }} data-id="element-1278" data-mp="3uzhpa9tg845k" />
      </div>
      <span className="text-xs text-slate-500 font-medium tabular-nums w-8 text-right" data-id="element-1279" data-mp="3l3xyg1tqrvno">
        {progress}%
      </span>
    </div>;
}
// --- Sortable Header ---
type SortableHeaderProps = {
  label: string;
  field: SortField;
  sortField: SortField;
  sortOrder: SortOrder;
  onSort: (field: SortField) => void;
  className?: string;
};
function SortableHeader({
  label,
  field,
  sortField,
  sortOrder,
  onSort,
  className = ''
}: SortableHeaderProps) {
  const isActive = sortField === field;
  return <th className={`py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors group ${className}`} onClick={() => onSort(field)} aria-sort={isActive ? sortOrder === 'asc' ? 'ascending' : 'descending' : 'none'} data-id="element-1280" data-mp="3kxxb9fuxtvvh">
      <div className="flex items-center gap-1" data-id="element-1281" data-mp="2x8cnmjnuaafv">
        {label}
        {isActive ? sortOrder === 'asc' ? <ChevronUp className="w-3 h-3 text-[#14466B]" data-id="element-1282" data-mp="31yjc8jicpuy4" /> : <ChevronDown className="w-3 h-3 text-[#14466B]" data-id="element-1283" data-mp="320chbudbg740" /> : <ChevronDown className="w-3 h-3 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1284" data-mp="24jnnonyisb5p" />}
      </div>
    </th>;
}
// --- Filter Popover ---
type FilterPopoverProps = {
  filterDepartment: string;
  setFilterDepartment: (department: string) => void;
  filterStatus: JobStatus | 'all';
  setFilterStatus: (status: JobStatus | 'all') => void;
  showValidatedOnly: boolean;
  setShowValidatedOnly: (value: boolean) => void;
  sortField: SortField;
  sortOrder: SortOrder;
  onSortChange: (field: SortField, order: SortOrder) => void;
};
function FilterPopover({
  filterDepartment,
  setFilterDepartment,
  filterStatus,
  setFilterStatus,
  showValidatedOnly,
  setShowValidatedOnly,
  sortField,
  sortOrder,
  onSortChange
}: FilterPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);
  const activeFilterCount = (filterDepartment ? 1 : 0) + (filterStatus !== 'all' ? 1 : 0) + (showValidatedOnly ? 1 : 0);
  return <div className="relative" ref={popoverRef} data-id="element-1285" data-mp="1p24a0js96j3p">
      <button onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 hover:border-slate-300 bg-white text-sm font-medium text-slate-700 transition-colors whitespace-nowrap" data-id="element-1286" data-mp="1aac6d51tmtht">
        <SlidersHorizontal className="w-4 h-4" data-id="element-1287" data-mp="11cs2rpghkl41" />
        Filters
        {activeFilterCount > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[#14466B] text-white text-xs font-semibold" data-id="element-1288" data-mp="16b3btxyyirl7">
            {activeFilterCount}
          </span>}
      </button>

      <AnimatePresence data-id="element-1289" data-mp="wcnidocgj68j">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.15
      }} className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden" data-id="element-1290" data-mp="wdovdyd1a2sw">
            <div className="p-4 border-b border-slate-100" data-id="element-1291" data-mp="2k8tzmhar2m3c">
              <h3 className="font-semibold text-slate-900 text-sm" data-id="element-1292" data-mp="2kbg8qldah9no">
                Filters & Sort
              </h3>
            </div>

            <div className="p-4 space-y-4 max-h-96 overflow-y-auto" data-id="element-1293" data-mp="2b96xtue3ze9r">
              {/* Department filter */}
              <div data-id="element-1294" data-mp="37z7l1oytxmoy">
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2" data-id="element-1295" data-mp="37xmqw0gfg5rm">
                  Department
                </label>
                <div className="flex flex-wrap gap-2" data-id="element-1296" data-mp="2y8wctq844rc9">
                  <button onClick={() => setFilterDepartment('')} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterDepartment === '' ? 'bg-[#14466B] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-1297" data-mp="2y7bmydwqtwkq">
                    All
                  </button>
                  {DEPARTMENTS.map(department => <button key={department} onClick={() => setFilterDepartment(department)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterDepartment === department ? 'bg-[#14466B] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-1298" data-mp="18g2vj24591qt">
                      {department}
                    </button>)}
                </div>
              </div>

              {/* Status Filter */}
              <div data-id="element-1299" data-mp="1rfqjrjtyb5st">
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2" data-id="element-1300" data-mp="1re5plvbjtovh">
                  Status
                </label>
                <div className="flex flex-wrap gap-2" data-id="element-1301" data-mp="2150bgykvumge">
                  {(['all', 'quote', 'pcc_in_progress', 'ongoing', 'closed', 'lost'] as const).map(status => <button key={status} onClick={() => setFilterStatus(status)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterStatus === status ? 'bg-[#14466B] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-1302" data-mp="255tylpqi3x88">
                      {status === 'all' ? 'All' : STATUS_CONFIG[status].label}
                    </button>)}
                </div>
              </div>

              <div data-id="element-1303" data-mp="1l21aliqz9cmf">
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2" data-id="element-1304" data-mp="1l4ndh52xb3yb">Validation</label>
                <button type="button" onClick={() => setShowValidatedOnly(!showValidatedOnly)} aria-pressed={showValidatedOnly} className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium transition-colors ${showValidatedOnly ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-1305" data-mp="1uzxje3wfzvhd"><CheckCircle2 className="h-3.5 w-3.5" data-id="element-1306" data-mp="1g4840olriglm" />Validated allocations only</button>
              </div>

              {/* Sort */}
              <div data-id="element-1307" data-mp="1badbcvo6irae">
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2" data-id="element-1308" data-mp="nmvs5oizd1j1">
                  Sort By
                </label>
                <select value={`${sortField}-${sortOrder}`} onChange={e => {
              const [field, order] = e.target.value.split('-') as [SortField, SortOrder];
              onSortChange(field, order);
            }} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none" data-id="element-1309" data-mp="jksd6px3ret0">
                  <option value="ref-asc" data-id="element-1310" data-mp="4jbr9lmrwuy3">Job ID (Low to High)</option>
                  <option value="ref-desc" data-id="element-1311" data-mp="s84ij4sgaemd">Job ID (High to Low)</option>
                  <option value="name-asc" data-id="element-1312" data-mp="s5s4h2ptcoel">Job Title (A-Z)</option>
                  <option value="name-desc" data-id="element-1313" data-mp="x5mul41rl9yl">Job Title (Z-A)</option>
                  <option value="projectDirector-asc" data-id="element-1314" data-mp="ia6twg6oift8">
                    Project Director (A-Z)
                  </option>
                  <option value="projectManager-asc" data-id="element-1315" data-mp="n7gqcfshjrqk">
                    Project Manager (A-Z)
                  </option>
                  <option value="value-desc" data-id="element-1316" data-mp="32yl46r644ptr">Budget (High to Low)</option>
                  <option value="value-asc" data-id="element-1317" data-mp="37zsbyq899p0f">Budget (Low to High)</option>
                  <option value="netProfit-desc" data-id="element-1318" data-mp="25j9tip3g4gol">Net Profit (High to Low)</option>
                  <option value="netProfit-asc" data-id="element-1319" data-mp="29kbw9fwknavb">Net Profit (Low to High)</option>
                  <option value="startDate-desc" data-id="element-1320" data-mp="29m6fmps72uxa">Start Date (Newest)</option>
                  <option value="startDate-asc" data-id="element-1321" data-mp="2ejy9hpcegr44">Start Date (Oldest)</option>
                  <option value="dateCreated-desc" data-id="element-1322" data-mp="2jhgj7hy8rad6">
                    Date Created (Newest)
                  </option>
                  <option value="dateCreated-asc" data-id="element-1323" data-mp="1lwc54evclig7">Date Created (Oldest)</option>
                </select>
              </div>
            </div>

            <div className="p-4 border-t border-slate-100 flex gap-2" data-id="element-1324" data-mp="1vsx70rc2mdft">
              <button onClick={() => {
            setFilterDepartment('');
            setFilterStatus('all');
            setShowValidatedOnly(false);
            setIsOpen(false);
          }} className="flex-1 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors" data-id="element-1325" data-mp="1vqu8tk0rle00">
                Clear
              </button>
              <button onClick={() => setIsOpen(false)} className="flex-1 px-3 py-2 rounded-lg text-sm font-medium bg-[#6A9A24] text-white hover:bg-[#587F1D] transition-colors" data-id="element-1326" data-mp="10k3h0luvvtkn">
                Apply
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
// --- Pagination ---
type PaginationProps = {
  page: number;
  totalPages: number;
  pageSize: number;
  setPageSize: (size: number) => void;
  setPage: (page: number) => void;
  showingFrom: number;
  showingTo: number;
  total: number;
};
function Pagination({
  page,
  totalPages,
  pageSize,
  setPageSize,
  setPage,
  showingFrom,
  showingTo,
  total
}: PaginationProps) {
  return <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-slate-100 mt-6" data-id="element-1327" data-mp="2wl91d91qvhp">
      <div className="flex items-center gap-2 text-sm text-slate-600" data-id="element-1328" data-mp="3u1b7cwhl9703">
        <span data-id="element-1329" data-mp="3q114hsp3ewpg">Show</span>
        <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} className="px-2 py-1 rounded border border-slate-200 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none" data-id="element-1330" data-mp="3q1u1w325w9ux">
          {PAGE_SIZE_OPTIONS.map(size => <option key={size} value={size} data-id="element-1331" data-mp="3kce1bt3mqo00">
              {size}
            </option>)}
        </select>
        <span data-id="element-1332" data-mp="36hc0vlhjxqul">
          Showing {showingFrom}–{showingTo} of {total}
        </span>
      </div>

      <div className="flex items-center gap-2" data-id="element-1333" data-mp="3bfwzxhl2i4hg">
        <button onClick={() => setPage(page - 1)} disabled={page === 1} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" aria-label="Previous page" data-id="element-1334" data-mp="2zbm9nhhqn80">
          <ChevronLeft className="w-4 h-4 text-slate-600" data-id="element-1335" data-mp="3pdlc8112k3aw" />
        </button>

        <div className="flex items-center gap-1" data-id="element-1336" data-mp="3ubn2b5wwx2vv">
          {Array.from({
          length: totalPages
        }, (_, i) => i + 1).filter(p => {
          if (totalPages <= 7) return true;
          if (p === 1 || p === totalPages) return true;
          if (p >= page - 1 && p <= page + 1) return true;
          return false;
        }).map((p, idx, arr) => {
          const showEllipsis = idx > 0 && p - arr[idx - 1] > 1;
          return <Fragment key={p} data-id="element-1337" data-mp="vz8pfcnb6fzb" data-mpi={idx}>
                  {showEllipsis && <span className="px-2 text-slate-400 text-sm" data-id="element-1338" data-mp="19reuo3oxb2jz">...</span>}
                  <button onClick={() => setPage(p)} className={`min-w-[32px] h-8 px-2 rounded-lg text-sm font-medium transition-colors ${page === p ? 'bg-[#14466B] text-white' : 'text-slate-600 hover:bg-slate-100'}`} data-id="element-1339" data-mp="14sv75yuolipi">
                    {p}
                  </button>
                </Fragment>;
        })}
        </div>

        <button onClick={() => setPage(page + 1)} disabled={page === totalPages} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" aria-label="Next page" data-id="element-1340" data-mp="r3041hiipqjp">
          <ChevronRight className="w-4 h-4 text-slate-600" data-id="element-1341" data-mp="22j8b9w8w9q1p" />
        </button>
      </div>
    </div>;
}
// --- Job Drawer ---
type JobDrawerProps = {
  job: Job | null;
  availableJobs: Job[];
  isAdmin: boolean;
  onClose: () => void;
  onSave: (data: Partial<Job>) => void;
  onAutoSave?: (data: Partial<Job>) => void;
  onDelete?: () => void;
};
type JobTab = 'details' | 'brief' | 'pcc' | 'pos' | 'notes' | 'financials' | 'history';
const drawerInputCls = 'w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none text-sm';
function JobDrawer({
  job,
  availableJobs,
  isAdmin,
  onClose,
  onSave,
  onAutoSave,
  onDelete
}: JobDrawerProps) {
  const isEdit = !!job;
  const [activeTab, setActiveTab] = useState<JobTab>('details');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newNote, setNewNote] = useState('');
  const briefFileInputRef = useRef<HTMLInputElement>(null);
  // Full working draft of the job
  const [draft, setDraft] = useState<Job>(() => ({
    id: job?.id || '',
    ref: job?.ref || '',
    name: job?.name || '',
    client: job?.client || '',
    departments: job?.departments || DEFAULT_JOB_DEPARTMENTS,
    status: job?.status || 'quote',
    value: job?.value || 0,
    projectDirector: job?.projectDirector || '',
    projectManager: job?.projectManager || '',
    startDate: job?.startDate || '',
    endDate: job?.endDate || '',
    dateCreated: job?.dateCreated || new Date().toISOString().split('T')[0],
    assignedTo: job?.assignedTo || '',
    description: job?.description || '',
    progress: job?.progress || 0,
    departmentBudgets: job?.departmentBudgets,
    likelihood: job?.likelihood ?? 50,
    poRequired: job?.poRequired ?? false,
    pcc: job?.pcc || makePcc(),
    purchaseOrders: job?.purchaseOrders || [],
    notes: job?.notes || [],
    attachments: job?.attachments || [],
    timesheets: job?.timesheets || [],
    expenses: job?.expenses || [],
    invoices: job?.invoices || [],
    mileageEntries: job?.mileageEntries || [],
    activity: job?.activity || (job ? createInitialJobActivity(job) : []),
    invoiced: job?.invoiced ?? 0,
    street: job?.street || '',
    line2: job?.line2 || '',
    city: job?.city || '',
    county: job?.county || '',
    postcode: job?.postcode || '',
    fromReferral: job?.fromReferral ?? false,
    referralMethod: job?.referralMethod || '',
    referringClient: job?.referringClient || '',
    parentJobId: job?.parentJobId,
    parentJobRef: job?.parentJobRef
  }));
  const update = (patch: Partial<Job>) => {
    setDraft(d => ({
      ...d,
      ...patch
    }));
    setError(null);
  };
  const addBriefFiles = (files: FileList | null) => {
    if (!files) return;
    const attachments = Array.from(files).map(file => ({
      id: Math.random().toString(36).slice(2, 11),
      name: file.name,
      size: file.size > 1024 * 1024 ? `${(file.size / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(file.size / 1024))} KB`,
      uploadedAt: new Date().toISOString()
    }));
    update({
      attachments: [...(draft.attachments || []), ...attachments]
    });
    attachments.forEach(attachment => recordActivity('File added', attachment.name));
    if (briefFileInputRef.current) briefFileInputRef.current.value = '';
  };
  const handleParentJobChange = (parentId: string) => {
    const parent = availableJobs.find(item => item.id === parentId);
    if (!parent) {
      update({
        parentJobId: undefined,
        parentJobRef: undefined
      });
      return;
    }
    update({
      parentJobId: parent.id,
      parentJobRef: parent.ref,
      name: parent.name,
      client: parent.client,
      departments: [...parent.departments],
      projectDirector: parent.projectDirector,
      projectManager: parent.projectManager,
      assignedTo: parent.assignedTo || '',
      poRequired: parent.poRequired ?? false,
      value: parent.value,
      likelihood: parent.likelihood ?? 50,
      startDate: parent.startDate,
      endDate: parent.endDate || '',
      description: parent.description || '',
      street: parent.street || '',
      line2: parent.line2 || '',
      city: parent.city || '',
      county: parent.county || '',
      postcode: parent.postcode || '',
      fromReferral: parent.fromReferral ?? false,
      referralMethod: parent.referralMethod || '',
      referringClient: parent.referringClient || ''
    });
  };
  const referringClients = useMemo(() => Array.from(new Set([...availableJobs.map(item => item.client), draft.referringClient || ''].filter(Boolean))).sort((a, b) => a.localeCompare(b)), [availableJobs, draft.referringClient]);
  const recordActivity = (action: string, detail?: string, actor: string = CURRENT_USER) => {
    const entry: JobActivity = {
      id: Math.random().toString(36).slice(2, 11),
      action,
      actor,
      occurredAt: new Date().toISOString(),
      detail
    };
    setDraft(d => ({
      ...d,
      activity: [entry, ...(d.activity || [])]
    }));
  };
  const updatePcc = (patch: Partial<PccInfo>) => setDraft(d => ({
    ...d,
    pcc: {
      ...(d.pcc || makePcc()),
      ...patch
    }
  }));
  const pcc = draft.pcc || makePcc();
  const poCount = draft.purchaseOrders?.length || 0;
  const checklistDone = pcc.checklist.every(c => c.done);
  const poRuleOk = !draft.poRequired || poCount > 0;
  const canCompletePcc = checklistDone && poRuleOk;
  const canBeOngoing = pcc.complete;
  // --- Status workflow helpers ---
  const currentFlowIndex = STATUS_FLOW.indexOf(draft.status);
  const nextStatus = currentFlowIndex >= 0 && currentFlowIndex < STATUS_FLOW.length - 1 ? STATUS_FLOW[currentFlowIndex + 1] : null;
  const progressBlockedByPcc = nextStatus === 'ongoing' && !canBeOngoing;
  const handleStatusChange = (next: JobStatus) => {
    if (next === 'ongoing' && !canBeOngoing) {
      setError('PCC must be completed before this job can move to Ongoing. See the PCC tab.');
      return;
    }
    const previousStatus = draft.status;
    update({
      status: next
    });
    recordActivity('Job changed status', `${STATUS_CONFIG[previousStatus].label} → ${STATUS_CONFIG[next].label}`);
  };
  // Auto-save edits to an existing job (debounced) without closing the drawer
  const didMount = useRef(false);
  useEffect(() => {
    if (!isEdit || !onAutoSave) return;
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    const t = setTimeout(() => onAutoSave(draft), 400);
    return () => clearTimeout(t);
  }, [draft, isEdit, onAutoSave]);
  const poTotal = (draft.purchaseOrders || []).reduce((sum, p) => sum + p.amount, 0);
  const handleSubmit = () => {
    if (!draft.name.trim()) {
      setActiveTab('details');
      setError('Job title is required.');
      return;
    }
    if (!draft.client.trim() || draft.departments.length === 0) {
      setActiveTab('details');
      setError('Client and at least one department are required.');
      return;
    }
    if (draft.status === 'ongoing' && !canBeOngoing) {
      setActiveTab('pcc');
      setError('PCC must be completed before this job can be Ongoing.');
      return;
    }
    onSave({
      ...draft,
      activity: [{
        id: Math.random().toString(36).slice(2, 11),
        action: 'Job was created',
        actor: CURRENT_USER,
        occurredAt: new Date().toISOString()
      }, ...(draft.activity || [])]
    });
  };
  const handleAddNote = () => {
    const body = newNote.trim();
    if (!body) return;
    const note: JobNote = {
      id: Math.random().toString(36).substr(2, 9),
      body,
      author: 'Matthew Carter',
      createdAt: new Date().toISOString()
    };
    update({
      notes: [note, ...(draft.notes || [])]
    });
    recordActivity('Note added', body);
    setNewNote('');
  };
  const tabs: {
    id: JobTab;
    label: string;
    icon: React.ReactNode;
    count?: number;
  }[] = [{
    id: 'details',
    label: 'Details',
    icon: <User className="w-4 h-4" data-id="element-1342" data-mp="1sx887wx0fnsu" />
  }, {
    id: 'history',
    label: 'History',
    icon: <History className="w-4 h-4" data-id="element-1343" data-mp="3p0mpn0560b8k" />,
    count: draft.activity?.length || 0
  }, {
    id: 'brief',
    label: 'Brief',
    icon: <FileText className="w-4 h-4" data-id="element-1344" data-mp="3kyj4fjsp1sa3" />
  }, {
    id: 'pcc',
    label: 'PCC',
    icon: <ClipboardCheck className="w-4 h-4" data-id="element-1345" data-mp="2u7nwpxwqdqg" />
  }, {
    id: 'pos',
    label: 'Purchase Orders',
    icon: <ShoppingCart className="w-4 h-4" data-id="element-1346" data-mp="3tzzbbhkavc9k" />,
    count: poCount
  }, {
    id: 'notes',
    label: 'Notes & Files',
    icon: <StickyNote className="w-4 h-4" data-id="element-1347" data-mp="36bg3f355x1i0" />,
    count: (draft.notes?.length || 0) + (draft.attachments?.length || 0)
  }, {
    id: 'financials',
    label: 'Financials',
    icon: <PoundSterling className="w-4 h-4" data-id="element-1348" data-mp="3g0zp0gk9f9gk" />
  }];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-end" onClick={onClose} data-id="element-1349" data-mp="3g1sp4yopr0pq">
      <motion.div initial={{
      x: '100%'
    }} animate={{
      x: 0
    }} exit={{
      x: '100%'
    }} transition={{
      type: 'tween',
      duration: 0.28,
      ease: [0.32, 0.72, 0, 1]
    }} onClick={e => e.stopPropagation()} className="w-full sm:max-w-4xl h-full bg-white shadow-2xl flex flex-col" role="dialog" aria-modal="true" data-id="element-1350" data-mp="2nhu5pyz1j64x">
        {/* Header */}
        <div className="flex items-start justify-between p-6 pb-4 border-b border-slate-100 gap-4" data-id="element-1351" data-mp="p47r1wrkfll9">
          <div className="min-w-0" data-id="element-1352" data-mp="t481z7fghenk">
            <div className="flex items-center gap-3 flex-wrap" data-id="element-1353" data-mp="1gtsog0atiu1l">
              <h2 className="text-xl font-bold text-slate-900 truncate" data-id="element-1354" data-mp="1guc9vqzog0kd">
                {isEdit ? draft.name || 'Untitled job' : 'New job'}
              </h2>
              <ProminentStatus status={draft.status} data-id="element-1355" data-mp="17tnuosyjru5w" />
            </div>
            <p className="text-sm text-slate-500 mt-0.5" data-id="element-1356" data-mp="1crp9h1oxx7bk">
              {isEdit ? <>
                  <span className="font-medium text-slate-600" data-id="element-1357" data-mp="3sj3dp1riyekz">
                    {draft.ref}
                  </span>
                  {' · '}
                  {draft.client || 'No client'}
                </> : 'Create a new job and capture its setup details.'}
            </p>
            {draft.departments.length > 0 && <div className="mt-3 flex flex-wrap items-center gap-1.5" aria-label="Assigned departments" data-id="element-1358" data-mp="bvz23r389929">
                {draft.departments.map(department => <span key={department} className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700" data-id="element-1359" data-mp="6x5n1u8al9zz">
                    {department}
                  </span>)}
              </div>}
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors flex-shrink-0" aria-label="Close" data-id="element-1360" data-mp="umggeqnbc243">
            <X className="w-5 h-5" data-id="element-1361" data-mp="qmpt4wwzw44b" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-4 sm:px-6 border-b border-slate-100 overflow-x-auto" data-id="element-1362" data-mp="ll8w5chmt1t0">
          {tabs.map(t => <button key={t.id} type="button" onClick={() => setActiveTab(t.id)} className={`relative flex items-center gap-2 px-3 py-3 text-sm font-medium transition-colors whitespace-nowrap ${activeTab === t.id ? 'text-[#14466B]' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1363" data-mp="3b8hogn77kwcl">
              {t.icon}
              {t.label}
              {typeof t.count === 'number' && t.count > 0 && <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold ${activeTab === t.id ? 'bg-[#14466B] text-white' : 'bg-slate-200 text-slate-700'}`} data-id="element-1364" data-mp="1ustwqueh2p7k">
                  {t.count}
                </span>}
              {activeTab === t.id && <motion.div layoutId="job-drawer-tab" className="absolute left-0 right-0 bottom-0 h-0.5 bg-[#14466B]" data-id="element-1365" data-mp="29hq5ktocs698" />}
            </button>)}
        </div>

        {/* Validation banner */}
        {error && <div className="mx-6 mt-4 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800" data-id="element-1366" data-mp="2ijfwzzi8j570">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-1367" data-mp="o5ye4psrywib" />
            <span data-id="element-1368" data-mp="o6862ixdrdqn">{error}</span>
          </div>}

        {/* Body */}
        <div className="flex-1 overflow-y-auto" data-id="element-1369" data-mp="1rhx8ngprcr6v">
          {activeTab === 'details' && <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1370" data-mp="1miaqcsv2okr7">
              {isEdit && <div data-id="element-1371" data-mp="20hahq89yzdr2">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1372" data-mp="20hsjtljbcm6w">
                    Job Number
                  </label>
                  <input type="text" disabled value={draft.ref} className={`${drawerInputCls} bg-slate-50 text-slate-500 cursor-not-allowed`} data-id="element-1373" data-mp="1wennk0pttgqp" />
                </div>}
              {!isEdit && <div className="md:col-span-2 rounded-xl border border-slate-200 bg-slate-50/70 p-4" data-id="element-1374" data-mp="25fu5me84izm2">
                  <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="parent-job" data-id="element-1375" data-mp="2k4882o1mom23">
                    Parent job <span className="text-slate-400 font-normal" data-id="element-1376" data-mp="2f787utrgmdiw">(optional)</span>
                  </label>
                  <select id="parent-job" value={draft.parentJobId || ''} onChange={e => handleParentJobChange(e.target.value)} className={drawerInputCls} data-id="element-1377" data-mp="2t4508zn8831w">
                    <option value="" data-id="element-1378" data-mp="37ty88b1z7y4r">No parent job</option>
                    {[...availableJobs].sort((a, b) => a.ref.localeCompare(b.ref, undefined, {
                numeric: true
              })).map(parent => <option key={parent.id} value={parent.id} data-id="element-1379" data-mp="2ysi9j0kixkgd">
                        {parent.ref} · {parent.name}
                      </option>)}
                  </select>
                  {draft.parentJobRef ? <p className="mt-2 text-xs text-[#14466B]" data-id="element-1380" data-mp="2p2f3nzsb0c0m">
                      New job ID: <span className="font-semibold" data-id="element-1381" data-mp="2p0c7ey0k5mng">{getNextChildJobRef(draft.parentJobRef, availableJobs)}</span>. Setup details have been copied and can be edited below.
                    </p> : <p className="mt-2 text-xs text-slate-500" data-id="element-1382" data-mp="2f5u2xqnbhn2l">
                      Select a parent job to copy its setup details and create a suffixed job ID.
                    </p>}
                </div>}
              <div className={isEdit ? '' : 'md:col-span-2'} data-id="element-1383" data-mp="264cnmahqhpey">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1384" data-mp="2b2xevkf0cyr9">
                  Job Title <span className="text-red-500" data-id="element-1385" data-mp="2ql6z8xhzlju4">*</span>
                </label>
                <input type="text" value={draft.name} onChange={e => update({
              name: e.target.value
            })} className={drawerInputCls} placeholder="Enter job title" data-id="element-1386" data-mp="2llu4lqkvj8gz" />
              </div>
              <div data-id="element-1387" data-mp="2cl5z5k9jqgfp">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1388" data-mp="2cjumeuy4hycg">
                  Client <span className="text-red-500" data-id="element-1389" data-mp="27m2w25d4mkad">*</span>
                </label>
                <input type="text" value={draft.client} onChange={e => update({
              client: e.target.value
            })} className={drawerInputCls} placeholder="Client name" data-id="element-1390" data-mp="22vcruvgfsnh9" />
              </div>
              <div className="md:col-span-2" data-id="element-1391" data-mp="1sxf4092hosp6">
                <span className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1392" data-mp="1syhqm4ss48za">
                  Departments <span className="text-red-500" data-id="element-1393" data-mp="1ous7vvysclm6">*</span>
                </span>
                <div className="flex flex-wrap gap-2 rounded-lg border border-slate-200 p-2" data-id="element-1394" data-mp="1jx9vfvlk7o9f">
                  {DEPARTMENTS.map(department => {
                const selected = draft.departments.includes(department);
                return <button key={department} type="button" aria-pressed={selected} onClick={() => update({
                  departments: selected ? draft.departments.filter(d => d !== department) : [...draft.departments, department]
                })} className={`inline-flex items-center rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${selected ? 'bg-[#14466B] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-1395" data-mp="4bpvgz7619xd">
                        {department}
                      </button>;
              })}
                </div>
                <p className="mt-1 text-xs text-slate-500" data-id="element-1396" data-mp="3ql0t6s6pnm1a">
                  Select every department delivering work on this job.
                </p>
              </div>
              <div data-id="element-1397" data-mp="2t392sx7wutf5">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1398" data-mp="2y3wu7k01xc9w">
                  Project Director <span className="text-red-500" data-id="element-1399" data-mp="27uiujhrzzgtu">*</span>
                </label>
                <input type="text" value={draft.projectDirector} onChange={e => update({
              projectDirector: e.target.value
            })} className={drawerInputCls} placeholder="Director name" data-id="element-1400" data-mp="27sg1spzgn9lf" />
              </div>
              <div data-id="element-1401" data-mp="23s5xriv8at97">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1402" data-mp="23rmdvwm0pusj">
                  Project Manager <span className="text-red-500" data-id="element-1403" data-mp="1p2q8k7bcivvm">*</span>
                </label>
                <input type="text" value={draft.projectManager} onChange={e => update({
              projectManager: e.target.value
            })} className={drawerInputCls} placeholder="Manager name" data-id="element-1404" data-mp="1tv17p7y9idn4" />
              </div>
              <div data-id="element-1405" data-mp="1k5ht4ehl15y2">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1406" data-mp="1k4y7onsq3zfa">
                  Budget (£) <span className="text-red-500" data-id="element-1407" data-mp="165p4copm8zsq">*</span>
                </label>
                <div className="relative" data-id="element-1408" data-mp="1b2ozi4kxgi52">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 pointer-events-none" data-id="element-1409" data-mp="1b5uo7ipn9i09">
                    £
                  </span>
                  <input type="text" inputMode="numeric" value={draft.value ? draft.value.toLocaleString('en-GB') : ''} onChange={e => {
                const digits = e.target.value.replace(/[^\d]/g, '');
                update({
                  value: digits ? Number(digits) : 0
                });
              }} className={`${drawerInputCls} pl-7`} placeholder="0" data-id="element-1410" data-mp="mkolnlj6izrg" />
                </div>
              </div>
              <div data-id="element-1411" data-mp="15fwwurn7ya4t">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1412" data-mp="1k6vz7kxgo2fy">
                  Likelihood (%)
                </label>
                <div className="relative" data-id="element-1413" data-mp="1f8u8cdtsmytx">
                  <input type="number" min="0" max="100" value={draft.likelihood ?? 0} onChange={e => {
                const n = Number(e.target.value);
                update({
                  likelihood: Math.max(0, Math.min(100, n || 0))
                });
              }} className={`${drawerInputCls} pr-8`} placeholder="0" data-id="element-1414" data-mp="1fa59s6zrnuhv" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 pointer-events-none" data-id="element-1415" data-mp="217qfv9lfq8b8">
                    %
                  </span>
                </div>
              </div>
              <div data-id="element-1416" data-mp="2ayb90hicakmr">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1417" data-mp="2ayurbzbwjb1b">
                  Start Date
                </label>
                <input type="date" value={draft.startDate} onChange={e => update({
              startDate: e.target.value
            })} className={drawerInputCls} data-id="element-1418" data-mp="2f20v90bjkq4m" />
              </div>
              <div data-id="element-1419" data-mp="2ovri60s0ssi7">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1420" data-mp="2twxavylokfrq">
                  End Date
                </label>
                <input type="date" value={draft.endDate} onChange={e => update({
              endDate: e.target.value
            })} className={drawerInputCls} data-id="element-1421" data-mp="ukl1t1vxbg8w" />
              </div>
              {/* Job address */}
              <div className="md:col-span-2 pt-2" data-id="element-1422" data-mp="3ctz7y3n57vi9">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5" data-id="element-1423" data-mp="382pkn9p2h49j">
                  <MapPin className="w-4 h-4" data-id="element-1424" data-mp="3402payt6t17l" />
                  Job Address
                </h3>
              </div>
              <div className="md:col-span-2" data-id="element-1425" data-mp="2z32haics1odu">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1426" data-mp="2yzy8f7oda4gu">
                  Street
                </label>
                <input type="text" value={draft.street} onChange={e => update({
              street: e.target.value
            })} className={drawerInputCls} placeholder="Street address" data-id="element-1427" data-mp="3rq6qz0mteqvy" />
              </div>
              <div className="md:col-span-2" data-id="element-1428" data-mp="3hr7grdmein6u">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1429" data-mp="3humxekvq44ad">
                  Line 2
                </label>
                <input type="text" value={draft.line2} onChange={e => update({
              line2: e.target.value
            })} className={drawerInputCls} placeholder="Apartment, suite, unit (optional)" data-id="element-1430" data-mp="26ckhprb9i556" />
              </div>
              <div data-id="element-1431" data-mp="2vxeka0ls4ms">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1432" data-mp="2wf6iug8fwow">
                  City
                </label>
                <input type="text" value={draft.city} onChange={e => update({
              city: e.target.value
            })} className={drawerInputCls} placeholder="City" data-id="element-1433" data-mp="3q87uchpun4a1" />
              </div>
              <div data-id="element-1434" data-mp="hqjz56auawxc">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1435" data-mp="lui25s3acrvo">
                  County
                </label>
                <input type="text" value={draft.county} onChange={e => update({
              county: e.target.value
            })} className={drawerInputCls} placeholder="County" data-id="element-1436" data-mp="7tfcl5d30zg0" />
              </div>
              <div data-id="element-1437" data-mp="2sjocv92n9rrh">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1438" data-mp="2sj4ujr9311cx">
                  Postcode
                </label>
                <input type="text" value={draft.postcode} onChange={e => update({
              postcode: e.target.value
            })} className={drawerInputCls} placeholder="Postcode" data-id="element-1439" data-mp="2pzrlnna2ebyo" />
              </div>

              {/* Referral */}
              <div className="md:col-span-2" data-id="element-1440" data-mp="2yzeqkj205zwh">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1441" data-mp="2u1ll5s8ix9x9">
                  Referral method
                </label>
                <select value={draft.referralMethod} onChange={e => update({
              referralMethod: e.target.value,
              referringClient: e.target.value === 'Referred by client' ? draft.referringClient : ''
            })} className={drawerInputCls} data-id="element-1442" data-mp="26d2d9dtdyz5p">
                  <option value="" data-id="element-1443" data-mp="2bbdlxl7y33mc">Select a method…</option>
                  {REFERRAL_METHODS.map(m => <option key={m} value={m} data-id="element-1444" data-mp="2b9aowh8dka84">
                      {m}
                    </option>)}
                </select>
              </div>
              {draft.referralMethod === 'Referred by client' && <div className="md:col-span-2" data-id="element-1445" data-mp="1ikls2uecqr26">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1446" data-mp="3ino4x7x4kyxz">Referring client</label>
                  <select value={draft.referringClient || ''} onChange={e => update({
              referringClient: e.target.value
            })} className={drawerInputCls} data-id="element-1447" data-mp="3nmhhmy4l8lx3">
                    <option value="" data-id="element-1448" data-mp="187tmq4klfwu">Select the client who referred this job…</option>
                    {referringClients.map(client => <option key={client} value={client} data-id="element-1449" data-mp="2zqd2xfk3zreu">{client}</option>)}
                  </select>
                </div>}
            </div>}

          {activeTab === 'brief' && <div className="p-6 space-y-5" data-id="element-1450" data-mp="34r0wa7vt8kc8">
              <div data-id="element-1451" data-mp="39nrize24ee6m">
                <h3 className="text-base font-semibold text-slate-900" data-id="element-1452" data-mp="39n83s53uu3wa">
                  Job brief
                </h3>
                <p className="text-xs text-slate-500 mt-0.5" data-id="element-1453" data-mp="3dqxlcam43l7t">
                  A short summary of the scope and intent of this job
                </p>
              </div>
              <div data-id="element-1454" data-mp="sy2kwsbznfo5">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1455" data-mp="sxkgh8f69v55">
                  Brief summary
                </label>
                <textarea value={draft.description} onChange={e => update({
              description: e.target.value
            })} rows={10} className={`${drawerInputCls} resize-y`} placeholder="Brief job summary" data-id="element-1456" data-mp="29g2yvahimjc1" />
              </div>
              <div data-id="element-1457" data-mp="1zp8ao0kpl9ny">
                <div className="mb-2 flex items-center justify-between" data-id="element-1458" data-mp="1zpri2ncqgfnq">
                  <label className="text-sm font-medium text-slate-700" data-id="element-1459" data-mp="1urzu24f7ldot">Brief files</label>
                  <button type="button" onClick={() => briefFileInputRef.current?.click()} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#14466B] hover:underline" data-id="element-1460" data-mp="1pqsirvduxwde">
                    <Upload className="h-3.5 w-3.5" data-id="element-1461" data-mp="1grxckjatcma3" />Add file
                  </button>
                  <input ref={briefFileInputRef} type="file" multiple className="hidden" onChange={event => addBriefFiles(event.target.files)} data-id="element-1462" data-mp="3g3zw1tjftgnr" />
                </div>
                {(draft.attachments || []).length === 0 ? <p className="rounded-lg border border-dashed border-slate-200 px-3 py-3 text-sm text-slate-400" data-id="element-1463" data-mp="2jzaviix6o2dk">No brief files attached yet.</p> : <div className="space-y-2" data-id="element-1464" data-mp="2oydyzyxim0jf">
                    {(draft.attachments || []).map(attachment => <div key={attachment.id} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2.5" data-id="element-1465" data-mp="2a980yeaeygd4">
                        <FileText className="h-4 w-4 flex-shrink-0 text-slate-400" data-id="element-1466" data-mp="2f715z3zzd4bt" />
                        <div className="min-w-0 flex-1" data-id="element-1467" data-mp="2181hbwcgwpfn"><p className="truncate text-sm text-slate-800" data-id="element-1468" data-mp="25ay7q9891pu5">{attachment.name}</p><p className="text-xs text-slate-400" data-id="element-1469" data-mp="25c98s1ab8jhk">{attachment.size}</p></div>
                        <button type="button" onClick={() => update({
                  attachments: (draft.attachments || []).filter(item => item.id !== attachment.id)
                })} className="rounded-lg p-1.5 text-slate-300 transition-colors hover:bg-rose-50 hover:text-rose-600" aria-label={`Remove ${attachment.name}`} data-id="element-1470" data-mp="1rcq119leva5s"><X className="h-4 w-4" data-id="element-1471" data-mp="3qoj2bif8f1ob" /></button>
                      </div>)}
                  </div>}
              </div>
            </div>}

          {activeTab === 'pcc' && <div className="p-6 space-y-6" data-id="element-1472" data-mp="1a1ul3xbii0kb">
              {/* PCC status banner */}
              <div className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${pcc.complete ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`} data-id="element-1473" data-mp="19zq9et7dfx9h">
                {pcc.complete ? <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" data-id="element-1474" data-mp="1jypiudzynwxj" /> : <ClipboardCheck className="w-5 h-5 text-slate-400 mt-0.5" data-id="element-1475" data-mp="1xqm3xy37p6kf" />}
                <div className="min-w-0" data-id="element-1476" data-mp="1ssklnfdm1ba0">
                  <p className="text-sm font-medium text-slate-900" data-id="element-1477" data-mp="1srss0a3kpnmd">
                    {pcc.complete ? 'PCC complete' : 'PCC in progress'}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5" data-id="element-1478" data-mp="22pyuastakvrr">
                    {pcc.complete ? `Approved by ${pcc.approvedBy || 'admin'}${pcc.approvedAt ? ` on ${new Date(pcc.approvedAt).toLocaleDateString('en-GB')}` : ''}.` : 'Complete all checklist items, the credit check, and any required purchase orders to finish PCC.'}
                  </p>
                </div>
              </div>

              {/* Checklist */}
              <div data-id="element-1479" data-mp="35irmim240gsf">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2" data-id="element-1480" data-mp="35jb4u3vo976z">
                  Checklist
                </h3>
                <div className="space-y-1.5" data-id="element-1481" data-mp="3ehmrxvxbxmuo">
                  {pcc.checklist.map(item => {
                const isCredit = item.id === 'credit';
                // The credit-check item can only be ticked once a score and
                // date checked have both been recorded.
                const creditReady = !!(pcc.creditCheckScore || '').trim() && !!(pcc.creditCheckDate || '').trim();
                const toggleBlocked = isCredit && !item.done && !creditReady;
                return <div key={item.id} className="rounded-lg border border-slate-200 overflow-hidden" data-id="element-1482" data-mp="27tq1dvpwxnrx">
                        <button type="button" disabled={toggleBlocked} onClick={() => updatePcc({
                    checklist: pcc.checklist.map(c => c.id === item.id ? {
                      ...c,
                      done: !c.done
                    } : c)
                  })} className={`w-full flex items-center gap-3 px-3 py-2.5 transition-colors text-left ${toggleBlocked ? 'cursor-not-allowed' : 'hover:bg-slate-50'}`} title={toggleBlocked ? 'Enter a credit score and date checked before marking this complete' : undefined} data-id="element-1483" data-mp="23se4r68mg2a8">
                          <span className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 transition-colors ${item.done ? 'bg-emerald-500 text-white' : `border-2 ${toggleBlocked ? 'border-slate-200' : 'border-slate-300'}`}`} data-id="element-1484" data-mp="19zfa8p7riq5z">
                            {item.done && <Check className="w-3.5 h-3.5" data-id="element-1485" data-mp="15ybqvsqiac00" />}
                          </span>
                          <span className={`text-sm ${item.done ? 'text-slate-500 line-through' : toggleBlocked ? 'text-slate-400' : 'text-slate-700'}`} data-id="element-1486" data-mp="2vdt30azn1j6o">
                            {item.label}
                          </span>
                          {isCredit && <span className="ml-auto flex items-center gap-1.5 text-xs font-medium text-slate-400" data-id="element-1487" data-mp="2xyr4hz1ethg5">
                              <ShieldCheck className="w-3.5 h-3.5" data-id="element-1488" data-mp="2tv2ul9pb29rq" />
                              Details
                            </span>}
                        </button>

                        {/* Credit check details live within the credit tick box */}
                        {isCredit && <div className="border-t border-slate-200 bg-slate-50/60 px-3 py-3 space-y-3" data-id="element-1489" data-mp="3hkx5hlq2do9c">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1490" data-mp="3co6rr51qf4r3">
                              <div data-id="element-1491" data-mp="37vdm8eyfvsdy">
                                <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1492" data-mp="37uayuh0bs82s">
                                  Score
                                </label>
                                <input type="text" inputMode="numeric" value={pcc.creditCheckScore || ''} onChange={e => updatePcc({
                          creditCheckScore: e.target.value
                        })} className={drawerInputCls} placeholder="e.g. 92" data-id="element-1493" data-mp="47u5e7v0yvcd" />
                              </div>
                              <div data-id="element-1494" data-mp="br3dzyvpiq0d">
                                <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1495" data-mp="bsee9opy1fmq">
                                  Date checked
                                </label>
                                <input type="date" value={pcc.creditCheckDate || ''} onChange={e => updatePcc({
                          creditCheckDate: e.target.value
                        })} className={drawerInputCls} data-id="element-1496" data-mp="lmoaxilx5039" />
                              </div>
                              <div className="sm:col-span-2" data-id="element-1497" data-mp="zh75x8zj91bj">
                                <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1498" data-mp="14ez8cwxt05tz">
                                  Notes
                                </label>
                                <textarea value={pcc.creditCheckNotes || ''} onChange={e => updatePcc({
                          creditCheckNotes: e.target.value
                        })} rows={2} className={`${drawerInputCls} resize-none`} placeholder="Credit agency, any conditions..." data-id="element-1499" data-mp="19cil1seusajt" />
                              </div>
                            </div>
                            {toggleBlocked && <p className="text-xs text-amber-700 flex items-center gap-1.5" data-id="element-1500" data-mp="2jwc02efoq9b6">
                                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" data-id="element-1501" data-mp="2fw1uv3zpvmxd" />
                                Enter a credit score and date checked to mark
                                this item complete.
                              </p>}
                          </div>}
                      </div>;
              })}
                </div>
              </div>

              {/* Approval / completion */}
              <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-id="element-1502" data-mp="1w67yecm24jmt">
                {pcc.complete && pcc.approvedBy && <p className="text-sm text-slate-600 flex items-center gap-1.5" data-id="element-1503" data-mp="3cnlwskqg3yc8">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-1504" data-mp="37mg58q8iuh4a" />
                    Approved by{' '}
                    <span className="font-medium text-slate-900" data-id="element-1505" data-mp="3g5t2nky04yti">
                      {pcc.approvedBy}
                    </span>
                    {pcc.approvedAt ? ` on ${new Date(pcc.approvedAt).toLocaleDateString('en-GB')}` : ''}
                  </p>}
                {!poRuleOk && <p className="text-xs text-amber-700 flex items-center gap-1.5" data-id="element-1506" data-mp="3b55gbckpx65m">
                    <AlertTriangle className="w-3.5 h-3.5" data-id="element-1507" data-mp="2pv77kiev0oc" />
                    This client requires a purchase order. Add at least one on
                    the Purchase Orders tab before completing PCC.
                  </p>}
                {!checklistDone && <p className="text-xs text-slate-500" data-id="element-1508" data-mp="3p2k98ra6jw2c">
                    Complete every checklist item to enable PCC sign-off.
                  </p>}
                {pcc.complete ? <button type="button" onClick={() => updatePcc({
              complete: false,
              approvedAt: undefined,
              approvedBy: undefined
            })} className="px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium transition-colors" data-id="element-1509" data-mp="lm3fyyamttoe">
                    Reopen PCC
                  </button> : <button type="button" disabled={!canCompletePcc} onClick={() => {
              updatePcc({
                complete: true,
                approvedAt: new Date().toISOString(),
                approvedBy: CURRENT_USER
              });
              recordActivity('PCC marked complete');
            }} className="px-4 py-2 rounded-lg bg-[#6A9A24] hover:bg-[#587F1D] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors flex items-center gap-2" data-id="element-1510" data-mp="qbnzxj5jn9my">
                    <CheckCircle2 className="w-4 h-4" data-id="element-1511" data-mp="2ne7hydtrnmu" />
                    Mark PCC complete
                  </button>}
              </div>
            </div>}

          {activeTab === 'pos' && <PurchaseOrdersPanel poRequired={!!draft.poRequired} setPoRequired={v => update({
          poRequired: v
        })} orders={draft.purchaseOrders || []} total={poTotal} onAdd={po => {
          update({
            purchaseOrders: [...(draft.purchaseOrders || []), po]
          });
          recordActivity('Purchase order added', `${po.number} · ${formatCurrency(po.amount)}`);
        }} onRemove={id => update({
          purchaseOrders: (draft.purchaseOrders || []).filter(p => p.id !== id)
        })} data-id="element-1512" data-mp="3prkn3j2jiy2m" />}

          {activeTab === 'notes' && <JobNotesPanel notes={draft.notes || []} attachments={draft.attachments || []} newNote={newNote} setNewNote={setNewNote} onAddNote={handleAddNote} onAddAttachment={a => {
          update({
            attachments: [...(draft.attachments || []), a]
          });
          recordActivity('File added', a.name);
        }} onRemoveAttachment={id => update({
          attachments: (draft.attachments || []).filter(a => a.id !== id)
        })} data-id="element-1513" data-mp="1mkd8vkdtqbhn" />}

          {activeTab === 'financials' && <FinancialsPanel value={draft.value || 0} departments={draft.departments} currentUser={CURRENT_USER} isAdmin={isAdmin} mileageEntries={draft.mileageEntries || []} onAddMileage={m => {
          update({
            mileageEntries: [m, ...(draft.mileageEntries || [])]
          });
          recordActivity('Mileage added', `${m.miles.toLocaleString('en-GB')} mi`, m.person);
        }} onRemoveMileage={id => update({
          mileageEntries: (draft.mileageEntries || []).filter(m => m.id !== id)
        })} timesheets={draft.timesheets || []} onAddTimesheet={ts => {
          update({
            timesheets: [ts, ...(draft.timesheets || [])]
          });
          recordActivity('Time logged', `${ts.hours}h`, ts.person);
        }} onRemoveTimesheet={id => update({
          timesheets: (draft.timesheets || []).filter(t => t.id !== id)
        })} expenses={draft.expenses || []} onAddExpense={exp => {
          update({
            expenses: [exp, ...(draft.expenses || [])]
          });
          recordActivity('Expense added', `${exp.category} · ${formatCurrency(exp.value)}`, exp.addedBy);
        }} onRemoveExpense={id => update({
          expenses: (draft.expenses || []).filter(e => e.id !== id)
        })} invoices={draft.invoices || []} onAddInvoice={inv => {
          update({
            invoices: [inv, ...(draft.invoices || [])]
          });
          recordActivity('Job invoice was raised', `${inv.category} · ${formatCurrency(inv.value)}`, inv.addedBy);
        }} onRemoveInvoice={id => update({
          invoices: (draft.invoices || []).filter(i => i.id !== id)
        })} data-id="element-1514" data-mp="17sl4u4jhcjv8" />}

          {activeTab === 'history' && <JobHistoryPanel activity={draft.activity || []} data-id="element-1515" data-mp="otywsxsu0cbh" />}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-200 flex items-center justify-end gap-3" data-id="element-1516" data-mp="tt1q5kzg8u3m">
          {isEdit ? <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2" data-id="element-1517" data-mp="ftisjm49l15k">
              <button type="button" onClick={() => handleStatusChange('lost')} disabled={draft.status === 'lost'} className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${draft.status === 'lost' ? 'border-slate-200 text-slate-400 cursor-not-allowed' : 'border-rose-200 text-rose-700 hover:bg-rose-50'}`} data-id="element-1518" data-mp="ksdjxcgary1a">
                <X className="w-4 h-4" data-id="element-1519" data-mp="3cyhl7wiaypyp" />
                {draft.status === 'lost' ? 'Marked as Lost' : 'Mark as Lost'}
              </button>
              <button type="button" onClick={() => nextStatus && handleStatusChange(nextStatus)} disabled={!nextStatus || progressBlockedByPcc} title={progressBlockedByPcc ? 'Complete PCC before moving to Ongoing' : undefined} className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${nextStatus && !progressBlockedByPcc ? 'bg-[#6A9A24] hover:bg-[#587F1D] text-white shadow-sm' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`} data-id="element-1520" data-mp="3mv444fr2j6y3">
                {progressBlockedByPcc ? <ShieldCheck className="w-4 h-4" data-id="element-1521" data-mp="efu73m21crx6" /> : <ChevronRight className="w-4 h-4" data-id="element-1522" data-mp="ecogg2drdhzc" />}
                {nextStatus ? progressBlockedByPcc ? 'Complete PCC to progress' : `Progress to ${STATUS_CONFIG[nextStatus].label}` : 'No further status'}
              </button>
            </div> : <div className="flex gap-2" data-id="element-1523" data-mp="1oydwjf6so6zu">
              <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium transition-colors" data-id="element-1524" data-mp="1oz5psjcx5smy">
                Cancel
              </button>
              <button type="button" onClick={handleSubmit} className="px-4 py-2 rounded-lg bg-[#6A9A24] hover:bg-[#587F1D] text-white text-sm font-medium transition-colors" data-id="element-1525" data-mp="2hp5m3ttctxe7">
                Create Job
              </button>
            </div>}
        </div>
      </motion.div>
    </motion.div>;
}
// --- Purchase Orders Panel ---
function PurchaseOrdersPanel({
  poRequired,
  setPoRequired,
  orders,
  total,
  onAdd,
  onRemove
}: {
  poRequired: boolean;
  setPoRequired: (v: boolean) => void;
  orders: PurchaseOrder[];
  total: number;
  onAdd: (po: PurchaseOrder) => void;
  onRemove: (id: string) => void;
}) {
  const [number, setNumber] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [file, setFile] = useState<{
    name: string;
    url: string;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleFile = (files: FileList | null) => {
    const selected = files?.[0];
    if (!selected) return;
    setFile({
      name: selected.name,
      url: URL.createObjectURL(selected)
    });
  };
  const add = () => {
    if (!number.trim()) return;
    onAdd({
      id: Math.random().toString(36).substr(2, 9),
      number: number.trim(),
      description: description.trim() || undefined,
      amount: Number(amount) || 0,
      date,
      fileName: file?.name,
      fileUrl: file?.url
    });
    setNumber('');
    setDescription('');
    setAmount('');
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  return <div className="p-6 space-y-5" data-id="element-1526" data-mp="3fwy6nl6cs5e8">
      <label className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-4 py-3" data-id="element-1527" data-mp="3fxzktyio1806">
        <span className="text-sm font-medium text-slate-700" data-id="element-1528" data-mp="3umm94pq9ta3j">
          Purchase orders required for this client
        </span>
        <button type="button" role="switch" aria-checked={poRequired} onClick={() => setPoRequired(!poRequired)} className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${poRequired ? 'bg-[#14466B]' : 'bg-slate-300'}`} data-id="element-1529" data-mp="3pt1dh4c5gs7i">
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-0.5 ${poRequired ? 'translate-x-[22px]' : 'translate-x-0.5'}`} data-id="element-1530" data-mp="he9hdkkq5fku" />
        </button>
      </label>

      {orders.length === 0 ? <div className="flex flex-col items-center justify-center text-center py-10 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50" data-id="element-1531" data-mp="2j6xpa78bcr6y">
          <ShoppingCart className="w-10 h-10 text-slate-300 mb-3" data-id="element-1532" data-mp="15b5rap94nkdf" />
          <p className="text-sm font-medium text-slate-900" data-id="element-1533" data-mp="15d0de6w4xij3">
            No purchase orders
          </p>
          <p className="text-xs text-slate-500 mt-1" data-id="element-1534" data-mp="1abjyy66tgsax">
            Add a purchase order below. Multiple POs can be tracked per job.
          </p>
        </div> : <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100" data-id="element-1535" data-mp="1j9459tjjdese">
          {orders.map(po => <div key={po.id} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors group" data-id="element-1536" data-mp="mlzflm6r3t14">
              <div className="min-w-0" data-id="element-1537" data-mp="vkupp9cx1n9p">
                <p className="text-sm font-medium text-slate-900" data-id="element-1538" data-mp="vjrw2to7l6q3">
                  {po.number}
                </p>
                <p className="text-xs text-slate-500 truncate" data-id="element-1539" data-mp="10d47g23vnh0w">
                  {new Date(po.date).toLocaleDateString('en-GB')}
                  {po.description ? ` · ${po.description}` : ''}
                </p>
                {po.fileUrl && <a href={po.fileUrl} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-[#14466B] hover:underline" data-id="element-1540" data-mp="7svyth0zhjbg"><Paperclip className="h-3 w-3" data-id="element-1541" data-mp="3rjbvkzxjd8ov" />{po.fileName || 'Open attachment'}</a>}
              </div>
              <div className="flex items-center gap-3 flex-shrink-0" data-id="element-1542" data-mp="3nevudbyvapfz">
                <span className="text-sm font-semibold text-slate-900 tabular-nums" data-id="element-1543" data-mp="3ii5hsym9ubzb">
                  {formatCurrency(po.amount)}
                </span>
                <button type="button" onClick={() => onRemove(po.id)} className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" aria-label="Remove purchase order" data-id="element-1544" data-mp="3dk3xkad406m9">
                  <Trash2 className="w-4 h-4" data-id="element-1545" data-mp="33q3mlrv5c75m" />
                </button>
              </div>
            </div>)}
          <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50" data-id="element-1546" data-mp="2urrtnj6t4xa0">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-1547" data-mp="2utm586w6vd1f">
              Total
            </span>
            <span className="text-sm font-bold text-slate-900 tabular-nums" data-id="element-1548" data-mp="2g4jcm1qthxq4">
              {formatCurrency(total)}
            </span>
          </div>
        </div>}

      {/* Add PO */}
      <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-id="element-1549" data-mp="2674yi8sd9l0z">
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-1550" data-mp="268zoi1ihw3bx">
          Add purchase order
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1551" data-mp="2yvu5upk0mml6">
          <input type="text" value={number} onChange={e => setNumber(e.target.value)} className={drawerInputCls} placeholder="PO number *" data-id="element-1552" data-mp="2yxnc43qpv4sn" />
          <input type="number" min="0" value={amount} onChange={e => setAmount(e.target.value)} className={drawerInputCls} placeholder="Amount" data-id="element-1553" data-mp="2p6am7hohaop9" />
          <input type="date" value={date} onChange={e => setDate(e.target.value)} className={drawerInputCls} data-id="element-1554" data-mp="2u30zxyct987i" />
          <input type="text" value={description} onChange={e => setDescription(e.target.value)} className={drawerInputCls} placeholder="Description" data-id="element-1555" data-mp="3hsbtauru00bm" />
          <div className="sm:col-span-2" data-id="element-1556" data-mp="1b5e4n5t43452">
            <input ref={fileInputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={event => handleFile(event.target.files)} data-id="element-1557" data-mp="1b43219bek2fj" />
            <div className="flex min-h-[38px] items-center gap-2" data-id="element-1558" data-mp="1kw8t8gttyfto">
              <button type="button" onClick={() => fileInputRef.current?.click()} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50" data-id="element-1559" data-mp="1fww5ltv4x4q1"><Upload className="h-3.5 w-3.5" data-id="element-1560" data-mp="n90ed4oc450n" />{file ? 'Replace file' : 'Add file'}</button>
              {file && <span className="inline-flex min-w-0 items-center gap-1.5 text-xs text-slate-600" data-id="element-1561" data-mp="1188d5wgl5b4b"><FileText className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" data-id="element-1562" data-mp="x6mx628r8gpw" /><span className="truncate" data-id="element-1563" data-mp="x8hihhnxuaui">{file.name}</span></span>}
            </div>
          </div>
        </div>
        <button type="button" onClick={add} disabled={!number.trim()} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#6A9A24] hover:bg-[#587F1D] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors" data-id="element-1564" data-mp="2coztd2emwoo7">
          <Plus className="w-4 h-4" data-id="element-1565" data-mp="ayr7o52srm8k" />
          Add purchase order
        </button>
      </div>
    </div>;
}
function JobHistoryPanel({
  activity
}: {
  activity: JobActivity[];
}) {
  const orderedActivity = [...activity].sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime());
  return <div className="p-6" data-id="element-1566" data-mp="oouq0kyk2vsq">
      <div className="mb-6" data-id="element-1567" data-mp="orqivuvjquab">
        <h3 className="text-base font-semibold text-slate-900" data-id="element-1568" data-mp="tnxqrt7i6fuw">Job history</h3>
        <p className="mt-0.5 text-xs text-slate-500" data-id="element-1569" data-mp="ynk4s4v5o04r">
          A record of the key actions taken on this job.
        </p>
      </div>

      {orderedActivity.length === 0 ? <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 px-6 py-12 text-center" data-id="element-1570" data-mp="13lvfeht9yeo1">
          <History className="mb-3 h-10 w-10 text-slate-300" data-id="element-1571" data-mp="35d87u7e22u37" />
          <p className="text-sm font-medium text-slate-900" data-id="element-1572" data-mp="35fupizunupz5">No activity yet</p>
          <p className="mt-1 text-xs text-slate-500" data-id="element-1573" data-mp="1mk6r1slzk9ut">
            Actions taken on this job will appear here.
          </p>
        </div> : <ol className="relative space-y-0 before:absolute before:bottom-5 before:left-4 before:top-5 before:w-px before:bg-slate-200" data-id="element-1574" data-mp="1dqlca99tmsle">
          {orderedActivity.map(entry => <li key={entry.id} className="relative flex gap-4 pb-6 last:pb-0" data-id="element-1575" data-mp="18tbn8uqcgiy3">
              <span className="relative z-10 mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border-4 border-white bg-[#E8F0F5] text-[#14466B] shadow-sm" data-id="element-1576" data-mp="25ggdp4aye8qf">
                <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1577" data-mp="1wg9zfg7fl4q7" />
              </span>
              <div className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-4 py-3" data-id="element-1578" data-mp="1rirojk9usffk">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-4" data-id="element-1579" data-mp="l266mdckxmmq">
                  <div className="min-w-0" data-id="element-1580" data-mp="g1s398lxb0v1">
                    <p className="text-sm font-semibold text-slate-900" data-id="element-1581" data-mp="g561c4nk0nwo">
                      {entry.action}
                    </p>
                    {entry.detail && <p className="mt-0.5 truncate text-xs text-slate-500" data-id="element-1582" data-mp="3p1hx9utmgvny">
                        {entry.detail}
                      </p>}
                  </div>
                  <time className="flex-shrink-0 text-xs text-slate-400" dateTime={entry.occurredAt} data-id="element-1583" data-mp="1n47lijpj852">
                    {formatJobActivityDate(entry.occurredAt)}
                  </time>
                </div>
                <p className="mt-2 text-xs text-slate-600" data-id="element-1584" data-mp="bl0rayqpnbes">
                  By <span className="font-medium text-slate-800" data-id="element-1585" data-mp="bnmw4qm7vctb">{entry.actor}</span>
                </p>
              </div>
            </li>)}
        </ol>}
    </div>;
}
function formatJobActivityDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Date unavailable';
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

// --- Financials Panel (read-only roll-up) ---
function FinancialsPanel({
  value,
  departments,
  currentUser,
  isAdmin,
  mileageEntries,
  onAddMileage,
  onRemoveMileage,
  timesheets,
  onAddTimesheet,
  onRemoveTimesheet,
  expenses,
  onAddExpense,
  onRemoveExpense,
  invoices,
  onAddInvoice,
  onRemoveInvoice
}: {
  value: number;
  departments: string[];
  currentUser: string;
  isAdmin: boolean;
  mileageEntries: MileageEntry[];
  onAddMileage: (m: MileageEntry) => void;
  onRemoveMileage: (id: string) => void;
  timesheets: TimesheetEntry[];
  onAddTimesheet: (t: TimesheetEntry) => void;
  onRemoveTimesheet: (id: string) => void;
  expenses: Expense[];
  onAddExpense: (e: Expense) => void;
  onRemoveExpense: (id: string) => void;
  invoices: Invoice[];
  onAddInvoice: (i: Invoice) => void;
  onRemoveInvoice: (id: string) => void;
}) {
  // Only one list section expanded at a time
  const [openSection, setOpenSection] = useState<'mileage' | 'timesheets' | 'expenses' | 'invoices' | null>(null);
  const expensesTotal = expenses.reduce((sum, e) => sum + e.value, 0);
  const invoiced = invoices.reduce((sum, i) => sum + i.value, 0);
  const totalMiles = mileageEntries.reduce((sum, m) => sum + m.miles, 0);
  const mileageValue = totalMiles * MILEAGE_RATE;
  const totalHours = timesheets.reduce((sum, entry) => sum + entry.hours, 0);
  const timeValue = timesheets.reduce((sum, entry) => sum + getTimesheetCost(entry), 0);
  // Total deductions taken against what's been invoiced
  const totalDeductions = expensesTotal + mileageValue + timeValue;
  // Realised profit is invoiced revenue less all deductions.
  const profit = invoiced - totalDeductions;
  const profitPositive = profit >= 0;
  // Budget profit applies the same deductions to the total job budget.
  const budgetProfit = value - totalDeductions;
  const budgetProfitPositive = budgetProfit >= 0;
  return <div className="p-6 space-y-5" data-id="element-1586" data-mp="d4ww1ng3is4n">
      <div data-id="element-1587" data-mp="d3v8ijhw8d61">
        <h3 className="text-base font-semibold text-slate-900" data-id="element-1588" data-mp="d3liivwum60c">
          Financial roll-up
        </h3>
        <p className="text-xs text-slate-500 mt-0.5" data-id="element-1589" data-mp="3pp0ywssdik4d">
          Job value less everything billed against it
        </p>
      </div>

      {/* 1. Job value / invoice — the headline figure everything deducts from */}
      <div className="rounded-xl border border-[#14466B]/20 bg-[#14466B]/[0.04] px-4 py-4" data-id="element-1590" data-mp="2l4bn2c6dsxg">
        <div className="flex items-center justify-between gap-3" data-id="element-1591" data-mp="3umakfxcn6bmx">
          <div className="flex items-center gap-3 min-w-0" data-id="element-1592" data-mp="ch7ekaeupvlg">
            <span className="w-10 h-10 rounded-lg bg-[#14466B] text-white flex items-center justify-center flex-shrink-0" data-id="element-1593" data-mp="7iw1lqt9f4z0">
              <PoundSterling className="w-5 h-5" data-id="element-1594" data-mp="m8aa6wp0hidc" />
            </span>
            <div className="min-w-0" data-id="element-1595" data-mp="m9lecxmdcs4z">
              <p className="text-sm font-semibold text-slate-900" data-id="element-1596" data-mp="h8fmf20j98wi">Job value</p>
              <p className="text-xs text-slate-500" data-id="element-1597" data-mp="v875hh5wpfhd">
                {invoices.length > 0 ? `${formatCurrency(invoiced)} invoiced to date` : 'Nothing invoiced yet'}
              </p>
            </div>
          </div>
          <span className="text-2xl font-bold text-[#14466B] tabular-nums flex-shrink-0" data-id="element-1598" data-mp="2kqdtfb7xb0zo">
            {formatCurrency(value)}
          </span>
        </div>
        {/* Invoiced is the realised slice of the job value — keep it directly under the headline */}
        <div className="mt-3 border-t border-[#14466B]/10 -mx-4 px-0" data-id="element-1599" data-mp="2uhrnh3h3v8lf">
          <FinancialAccordionRow icon={<FileText className="w-4 h-4" data-id="element-1601" data-mp="2yitptt6bk0rm" />} label="Invoiced" count={invoices.length} total={formatCurrency(invoiced)} open={openSection === 'invoices'} onToggle={() => setOpenSection(s => s === 'invoices' ? null : 'invoices')} data-id="element-1600" data-mp="2ygha7mo1a2hq">
            <InvoicesPanel invoices={invoices} departments={departments} currentUser={currentUser} isAdmin={isAdmin} onAdd={onAddInvoice} onRemove={onRemoveInvoice} data-id="element-1602" data-mp="2aqwqh1k2317e" />
          </FinancialAccordionRow>
        </div>
      </div>

      {/* 2. Deductions — everything taken off the job value, listed beneath it */}
      <div className="space-y-2" data-id="element-1603" data-mp="3r4v9s6urt6ve">
        <div className="px-1" data-id="element-1604" data-mp="3r43fczcwtf6p">
          <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider" data-id="element-1605" data-mp="547dtjfc8abe">
            Deductions
          </span>
        </div>
        <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100" data-id="element-1606" data-mp="6fazud5n3sf">
          {/* Time logged (expandable) */}
          <FinancialAccordionRow icon={<Clock className="w-4 h-4" data-id="element-1608" data-mp="3niq3n2ni1xx3" />} label="Time logged" count={timesheets.length} total={`−${formatCurrency(timeValue)}`} subTotal={`${totalHours}h`} open={openSection === 'timesheets'} onToggle={() => setOpenSection(s => s === 'timesheets' ? null : 'timesheets')} data-id="element-1607" data-mp="3rhffgqsw37em">
            <TimesheetsPanel timesheets={timesheets} departments={departments} currentUser={currentUser} isAdmin={isAdmin} onAdd={onAddTimesheet} onRemove={onRemoveTimesheet} data-id="element-1609" data-mp="38kxoiblv7bjf" />
          </FinancialAccordionRow>

          {/* Expenses (expandable) */}
          <FinancialAccordionRow icon={<Receipt className="w-4 h-4" data-id="element-1611" data-mp="16rgj7el7imru" />} label="Expenses" count={expenses.length} total={`−${formatCurrency(expensesTotal)}`} open={openSection === 'expenses'} onToggle={() => setOpenSection(s => s === 'expenses' ? null : 'expenses')} data-id="element-1610" data-mp="16s9ijuhu69zy">
            <ExpensesPanel expenses={expenses} departments={departments} currentUser={currentUser} isAdmin={isAdmin} onAdd={onAddExpense} onRemove={onRemoveExpense} data-id="element-1612" data-mp="311y5w8689o14" />
          </FinancialAccordionRow>

          {/* Mileage (expandable) */}
          <FinancialAccordionRow icon={<Car className="w-4 h-4" data-id="element-1614" data-mp="2r98x5l22ao9h" />} label="Mileage" count={mileageEntries.length} total={`−${formatCurrency(mileageValue)}`} subTotal={`${totalMiles.toLocaleString('en-GB')} mi`} open={openSection === 'mileage'} onToggle={() => setOpenSection(s => s === 'mileage' ? null : 'mileage')} data-id="element-1613" data-mp="2rc4ub76358wv">
            <MileagePanel entries={mileageEntries} departments={departments} currentUser={currentUser} isAdmin={isAdmin} onAdd={onAddMileage} onRemove={onRemoveMileage} data-id="element-1615" data-mp="362lw4rmmu0ua" />
          </FinancialAccordionRow>

          {/* Total deductions (summary row) */}
          <div className="flex items-center justify-between gap-3 px-4 py-3 bg-slate-50" data-id="element-1616" data-mp="1zkp99hk6fu8r">
            <p className="text-sm font-semibold text-slate-900" data-id="element-1617" data-mp="1r2vm9wto8tde">
              Total deductions
            </p>
            <span className="text-sm font-bold text-rose-600 tabular-nums flex-shrink-0" data-id="element-1618" data-mp="1n0s0ofdag8ee">
              −{formatCurrency(totalDeductions)}
            </span>
          </div>
        </div>
      </div>

      {/* 3. Realised and budget profit use the same live deductions. */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2" data-id="element-1619" data-mp="18cnq5yoe336p">
        <div className={`rounded-xl border px-4 py-4 ${profitPositive ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`} data-id="element-1620" data-mp="18d71gwjjatbr">
          <div className="flex items-center justify-between gap-3" data-id="element-1621" data-mp="1i38n7hoezzni">
            <div className="flex items-center gap-3 min-w-0" data-id="element-1622" data-mp="1i4jrdilrv9f5">
              <span className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${profitPositive ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`} data-id="element-1623" data-mp="1db8i7athmgk2">
                {profitPositive ? <TrendingUp className="w-5 h-5" data-id="element-1624" data-mp="kmte3jbw9ini" /> : <TrendingDown className="w-5 h-5" data-id="element-1625" data-mp="3v9fejytjpu0z" />}
              </span>
              <div className="min-w-0" data-id="element-1626" data-mp="3vb8ktd08yc8g">
                <p className={`text-sm font-semibold ${profitPositive ? 'text-emerald-800' : 'text-rose-800'}`} data-id="element-1627" data-mp="42uq8muvl07n">
                  {profitPositive ? 'Net profit' : 'Net loss'}
                </p>
                <p className={`text-xs ${profitPositive ? 'text-emerald-600' : 'text-rose-600'}`} data-id="element-1628" data-mp="e10uhb45mifo">
                  Invoiced less time, expenses &amp; mileage
                </p>
              </div>
            </div>
            <span className={`text-xl font-bold tabular-nums flex-shrink-0 ${profitPositive ? 'text-emerald-700' : 'text-rose-700'}`} data-id="element-1629" data-mp="n172wikzws7z">
              {profit < 0 ? `−${formatCurrency(Math.abs(profit))}` : formatCurrency(profit)}
            </span>
          </div>
        </div>
        <div className={`rounded-xl border px-4 py-4 ${budgetProfitPositive ? 'bg-blue-50 border-blue-200' : 'bg-rose-50 border-rose-200'}`} data-id="element-1630" data-mp="11qv7bsoh46zx">
          <div className="flex items-center justify-between gap-3" data-id="element-1631" data-mp="15t9nu64o6uo1">
            <div className="flex items-center gap-3 min-w-0" data-id="element-1632" data-mp="2qb0mca94ri80">
              <span className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${budgetProfitPositive ? 'bg-blue-100 text-[#14466B]' : 'bg-rose-100 text-rose-700'}`} data-id="element-1633" data-mp="2q8eh4h9ppesy">
                {budgetProfitPositive ? <TrendingUp className="w-5 h-5" data-id="element-1634" data-mp="2hbnl24v5tzyd" /> : <TrendingDown className="w-5 h-5" data-id="element-1635" data-mp="2cbj7onpjtlcd" />}
              </span>
              <div className="min-w-0" data-id="element-1636" data-mp="39y8edav74m4k">
                <p className={`text-sm font-semibold ${budgetProfitPositive ? 'text-[#14466B]' : 'text-rose-800'}`} data-id="element-1637" data-mp="39x6qu6wzu75y">
                  {budgetProfitPositive ? 'Profit vs budget' : 'Loss vs budget'}
                </p>
                <p className={`text-xs ${budgetProfitPositive ? 'text-blue-700' : 'text-rose-600'}`} data-id="element-1638" data-mp="306u8mlcniq2z">
                  Job budget less time, expenses &amp; mileage
                </p>
              </div>
            </div>
            <span className={`text-xl font-bold tabular-nums flex-shrink-0 ${budgetProfitPositive ? 'text-[#14466B]' : 'text-rose-700'}`} data-id="element-1639" data-mp="3sqst7oe28qpd">
              {budgetProfit < 0 ? `−${formatCurrency(Math.abs(budgetProfit))}` : formatCurrency(budgetProfit)}
            </span>
          </div>
        </div>
      </div>
    </div>;
}
// Expandable roll-up row that reveals a list + inline add form when open
function FinancialAccordionRow({
  icon,
  label,
  count,
  total,
  subTotal,
  open,
  onToggle,
  children
}: {
  icon: React.ReactNode;
  label: string;
  count: number;
  total: string;
  subTotal?: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return <div data-id="element-1640" data-mp="17ffjp2ev59on">
      <button type="button" onClick={onToggle} aria-expanded={open} className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left" data-id="element-1641" data-mp="17fp9opzwrguc">
        <div className="flex items-center gap-3 min-w-0" data-id="element-1642" data-mp="1h84pbgnqftc2">
          <span className="w-9 h-9 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center flex-shrink-0" data-id="element-1643" data-mp="1v6b43egzp2lv">
            {icon}
          </span>
          <div className="min-w-0 flex items-center gap-2" data-id="element-1644" data-mp="1q66r3yfaiq0e">
            <p className="text-sm font-medium text-slate-900" data-id="element-1645" data-mp="29ucm1g99bxfz">{label}</p>
            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold bg-slate-200 text-slate-700" data-id="element-1646" data-mp="2et62nhjuc4kd">
              {count}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0" data-id="element-1647" data-mp="24svlxmylkf0g">
          <span className="text-right" data-id="element-1648" data-mp="1r31v4z3j7ko3">
            <span className="block text-sm font-semibold text-slate-900 tabular-nums" data-id="element-1649" data-mp="1r20iiq6vaq49">
              {total}
            </span>
            {subTotal && <span className="block text-xs text-slate-500 tabular-nums" data-id="element-1650" data-mp="1h5nti5m9p0fu">
                {subTotal}
              </span>}
          </span>
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-1651" data-mp="1m50cgfa0pnd5" />
        </div>
      </button>
      {open && <div className="border-t border-slate-100 bg-slate-50/50" data-id="element-1652" data-mp="1c61djof2163c">
          {children}
        </div>}
    </div>;
}
// Collapsible "add new item" section — separates viewing the list from the
// action of adding to it. Shows a single trigger line until expanded.
function AddItemSection({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  if (!open) {
    return <button type="button" onClick={() => setOpen(true)} className="w-full flex items-center gap-2 px-4 py-3 rounded-xl border border-dashed border-slate-300 text-sm font-medium text-[#14466B] hover:bg-[#14466B]/[0.04] hover:border-[#14466B]/40 transition-colors" data-id="element-1653" data-mp="304lfbwaeifrg">
        <Plus className="w-4 h-4" data-id="element-1654" data-mp="3nteee1mbl3qa" />
        {label}
      </button>;
  }
  return <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-id="element-1655" data-mp="3nrl8iojj6njc">
      <div className="flex items-center justify-between" data-id="element-1656" data-mp="3jr18nd7kp5ti">
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-1657" data-mp="2cdzil15ukhwr">
          {label}
        </h3>
        <button type="button" onClick={() => setOpen(false)} className="inline-flex items-center gap-1 text-xs font-medium text-slate-400 hover:text-slate-600 transition-colors" data-id="element-1658" data-mp="28ce9zs0cipsd">
          <X className="w-3.5 h-3.5" data-id="element-1659" data-mp="rvxdv4w4i58d" />
          Cancel
        </button>
      </div>
      {children}
    </div>;
}
// --- Mileage Panel ---
function MileagePanel({
  entries,
  departments,
  currentUser,
  isAdmin,
  onAdd,
  onRemove
}: {
  entries: MileageEntry[];
  departments: string[];
  currentUser: string;
  isAdmin: boolean;
  onAdd: (m: MileageEntry) => void;
  onRemove: (id: string) => void;
}) {
  const [person, setPerson] = useState(currentUser);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [miles, setMiles] = useState('');
  const [department, setDepartment] = useState('');
  const [notes, setNotes] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const numericMiles = Number(miles);
  const canAdd = !!person.trim() && numericMiles > 0 && !!department;
  const add = () => {
    if (!person.trim()) {
      setFormError('Enter who claimed the mileage.');
      return;
    }
    if (!miles || numericMiles <= 0) {
      setFormError('Enter the number of miles.');
      return;
    }
    if (!department) {
      setFormError('Select a department.');
      return;
    }
    onAdd({
      id: Math.random().toString(36).substr(2, 9),
      person: person.trim(),
      date,
      miles: numericMiles,
      department,
      notes: notes.trim() || undefined
    });
    setPerson(currentUser);
    setDate(new Date().toISOString().split('T')[0]);
    setMiles('');
    setDepartment('');
    setNotes('');
    setFormError(null);
  };
  return <div className="p-6 space-y-5" data-id="element-1660" data-mp="2i2fm2dipn1kl">
      {/* Mileage list */}
      {entries.length === 0 ? <div className="flex flex-col items-center justify-center text-center py-10 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50" data-id="element-1661" data-mp="2dae205faw6k0">
          <Car className="w-10 h-10 text-slate-300 mb-3" data-id="element-1662" data-mp="1piz18h369xav" />
          <p className="text-sm font-medium text-slate-900" data-id="element-1663" data-mp="1kkxcpgmz95s0">
            No mileage claimed
          </p>
          <p className="text-xs text-slate-500 mt-1" data-id="element-1664" data-mp="3658n0jh7vw">
            Add mileage below to claim it against this job.
          </p>
        </div> : <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100" data-id="element-1665" data-mp="4wheeustq0qz">
          {entries.map(m => {
        const canDelete = isAdmin || m.person === currentUser;
        return <div key={m.id} className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50 transition-colors group" data-id="element-1666" data-mp="3r7weh1amd3t2">
                <div className="min-w-0" data-id="element-1667" data-mp="ntscwd6msq5d">
                  <div className="flex items-center gap-2 flex-wrap" data-id="element-1668" data-mp="nsqzi2257rkh">
                    <p className="text-sm font-medium text-slate-900" data-id="element-1669" data-mp="9spqfzbq5dtx">
                      {m.person}
                    </p>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[11px] font-medium" data-id="element-1670" data-mp="dtjdyrl98qma">
                      {m.department}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500" data-id="element-1671" data-mp="152uwp43jmxx3">
                    {new Date(m.date).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              })}
                    {m.notes ? ` · ${m.notes}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0" data-id="element-1672" data-mp="qeq9bmtjpy7z">
                  <span className="text-right" data-id="element-1673" data-mp="mc3gpjp1w99q">
                    <span className="block text-sm font-semibold text-slate-900 tabular-nums" data-id="element-1674" data-mp="mdy372fz09fx">
                      {m.miles.toLocaleString('en-GB')} mi
                    </span>
                    <span className="block text-xs text-slate-500 tabular-nums" data-id="element-1675" data-mp="cf8h0xh1k2tv">
                      {formatCurrency(m.miles * MILEAGE_RATE)}
                    </span>
                  </span>
                  {canDelete && <button type="button" onClick={() => onRemove(m.id)} className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" aria-label="Delete mileage entry" data-id="element-1676" data-mp="26qx2lihs33n8">
                      <Trash2 className="w-4 h-4" data-id="element-1677" data-mp="2a9meu85pnyq5" />
                    </button>}
                </div>
              </div>;
      })}
        </div>}

      {/* Add mileage */}
      <AddItemSection label="Add new mileage" data-id="element-1678" data-mp="2j1xtyxvdjzqt">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1679" data-mp="2j37qvjqhr1vv">
          <div data-id="element-1680" data-mp="2o0hdysqer1gj">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1681" data-mp="2o10xuezmbzx7">
              Person *
            </label>
            <input type="text" value={person} onChange={e => {
            setPerson(e.target.value);
            setFormError(null);
          }} className={drawerInputCls} placeholder="Who claimed the mileage" data-id="element-1682" data-mp="1rdcskxkno1v2" />
          </div>
          <div data-id="element-1683" data-mp="25armaehxyvsu">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1684" data-mp="25bb4lwbi7m7e">
              Miles *
            </label>
            <input type="number" min="0" step="1" value={miles} onChange={e => {
            setMiles(e.target.value);
            setFormError(null);
          }} className={drawerInputCls} placeholder="0" data-id="element-1685" data-mp="3bqbq182wklzq" />
          </div>
          <div data-id="element-1686" data-mp="5p4c2eowod92">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1687" data-mp="5mi4if20lxqu">
              Department *
            </label>
            <select value={department} onChange={e => {
            setDepartment(e.target.value);
            setFormError(null);
          }} className={drawerInputCls} data-id="element-1688" data-mp="ff7fl8tnl9ln">
              <option value="" data-id="element-1689" data-mp="am4hqohufdzn">Select a department…</option>
              {departments.map(item => <option key={item} value={item} data-id="element-1690" data-mp="peyb9dty9unb">
                  {item}
                </option>)}
            </select>
          </div>
          <div data-id="element-1691" data-mp="yd9x72jvg49f">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1692" data-mp="yds36qwc5wuj">
              Date
            </label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} className={drawerInputCls} data-id="element-1693" data-mp="tfqkw86qi1k4" />
          </div>
          <div className="sm:col-span-2" data-id="element-1694" data-mp="306zgxvwaw6eb">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1695" data-mp="2v6bqbbbzhrkm">
              Notes
            </label>
            <input type="text" value={notes} onChange={e => setNotes(e.target.value)} className={drawerInputCls} placeholder="Optional notes" data-id="element-1696" data-mp="1aoste2y74mwj" />
          </div>
        </div>
        {formError && <p className="text-xs text-rose-600 flex items-center gap-1.5" data-id="element-1697" data-mp="11w93bqvyzqnb">
            <AlertTriangle className="w-3.5 h-3.5" data-id="element-1698" data-mp="15x1ckk4udvjl" />
            {formError}
          </p>}
        <button type="button" onClick={add} disabled={!canAdd} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#6A9A24] hover:bg-[#587F1D] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors" data-id="element-1699" data-mp="15uomfjoxkevg">
          <Plus className="w-4 h-4" data-id="element-1700" data-mp="ixmyc0xc1fe4" />
          Add mileage
        </button>
      </AddItemSection>
    </div>;
}
// --- Timesheets Panel ---
function TimesheetsPanel({
  timesheets,
  departments,
  currentUser,
  isAdmin,
  onAdd,
  onRemove
}: {
  timesheets: TimesheetEntry[];
  departments: string[];
  currentUser: string;
  isAdmin: boolean;
  onAdd: (t: TimesheetEntry) => void;
  onRemove: (id: string) => void;
}) {
  const {
    users
  } = useSettingsStore();
  const activeUsers = users.filter(user => user.status === 'active');
  const defaultUser = activeUsers.find(user => `${user.firstName} ${user.lastName}` === currentUser) || activeUsers[0];
  const [userId, setUserId] = useState(defaultUser?.id || '');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [hours, setHours] = useState('');
  const [department, setDepartment] = useState('');
  const [notes, setNotes] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const numericHours = Number(hours);
  const selectedUser = activeUsers.find(user => user.id === userId);
  const canAdd = !!selectedUser && numericHours > 0 && !!department;
  const add = () => {
    if (!selectedUser) {
      setFormError('Select who logged the time.');
      return;
    }
    if (!hours || numericHours <= 0) {
      setFormError('Enter the number of hours.');
      return;
    }
    if (!department) {
      setFormError('Select a department.');
      return;
    }
    onAdd({
      id: Math.random().toString(36).substr(2, 9),
      person: `${selectedUser.firstName} ${selectedUser.lastName}`,
      date,
      hours: numericHours,
      department,
      hourlyCost: selectedUser.hourlyCost,
      notes: notes.trim() || undefined
    });
    setUserId(defaultUser?.id || '');
    setDate(new Date().toISOString().split('T')[0]);
    setHours('');
    setDepartment('');
    setNotes('');
    setFormError(null);
  };
  return <div className="p-6 space-y-5" data-id="element-1701" data-mp="1ubp2vg4uer82">
      {/* Timesheet list */}
      {timesheets.length === 0 ? <div className="flex flex-col items-center justify-center text-center py-10 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50" data-id="element-1702" data-mp="2871486di7fxy">
          <Clock className="w-10 h-10 text-slate-300 mb-3" data-id="element-1703" data-mp="1fnkgo26kzxkj" />
          <p className="text-sm font-medium text-slate-900" data-id="element-1704" data-mp="1bn0l331np20i">No time logged</p>
          <p className="text-xs text-slate-500 mt-1" data-id="element-1705" data-mp="1bkxx0ok2diyf">
            Add a timesheet entry below to book time against this job.
          </p>
        </div> : <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100" data-id="element-1706" data-mp="3fqddnky0xeod">
          {timesheets.map(t => {
        const canDelete = isAdmin || t.person === currentUser;
        return <div key={t.id} className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50 transition-colors group" data-id="element-1707" data-mp="3pgy7kv2r5v0y">
                <div className="min-w-0" data-id="element-1708" data-mp="2rur1thuppwn0">
                  <div className="flex items-center gap-2 flex-wrap" data-id="element-1709" data-mp="2wrr7hrnngxdl">
                    <p className="text-sm font-medium text-slate-900" data-id="element-1710" data-mp="2wu3pu5xbl9r6">
                      {t.person}
                    </p>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[11px] font-medium" data-id="element-1711" data-mp="36pxbqfwxiflm">
                      {t.department}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500" data-id="element-1712" data-mp="lywt432s5m1t">
                    {new Date(t.date).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              })}
                    {t.notes ? ` · ${t.notes}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0" data-id="element-1713" data-mp="1wqckkbs9fkao">
                  <span className="text-right tabular-nums" data-id="element-1714" data-mp="1rsb4lzq4s13f">
                    <span className="block text-sm font-semibold text-slate-900" data-id="element-1715" data-mp="1mtrju2n9wvcn">{t.hours}h</span>
                    <span className="block text-xs text-slate-500" data-id="element-1716" data-mp="1iso0v79xij77">£{(t.hourlyCost ?? LEGACY_HOURLY_COST).toFixed(2)}/hr · {formatCurrency(getTimesheetCost(t))}</span>
                  </span>
                  {canDelete && <button type="button" onClick={() => onRemove(t.id)} className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" aria-label="Delete timesheet entry" data-id="element-1717" data-mp="18urt8pg7a6du">
                      <Trash2 className="w-4 h-4" data-id="element-1718" data-mp="337rccsjshipv" />
                    </button>}
                </div>
              </div>;
      })}
        </div>}

      {/* Add timesheet */}
      <AddItemSection label="Log new time" data-id="element-1719" data-mp="20t0n4m0i7oxm">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1720" data-mp="24vf2ux8vm8ko">
          <div data-id="element-1721" data-mp="1qwnqbf9nwuyn">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1722" data-mp="1qx70uawzgh2n">
              Person *
            </label>
            <select value={userId} onChange={e => {
            setUserId(e.target.value);
            setFormError(null);
          }} className={drawerInputCls} data-id="element-1723" data-mp="1vup7tvrfwm80">
              <option value="" data-id="element-1724" data-mp="1lxcaq9l17vin">Select a user…</option>
              {activeUsers.map(user => <option key={user.id} value={user.id} data-id="element-1725" data-mp="1lzfavmfwf513">{user.firstName} {user.lastName} · £{user.hourlyCost.toFixed(2)}/hr</option>)}
            </select>
          </div>
          <div data-id="element-1726" data-mp="1d5sfvyjim5sy">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1727" data-mp="1d5is8hly0aqf">
              Hours *
            </label>
            <input type="number" min="0" step="0.25" value={hours} onChange={e => {
            setHours(e.target.value);
            setFormError(null);
          }} className={drawerInputCls} placeholder="0" data-id="element-1728" data-mp="13738x0t5q5rh" />
          </div>
          <div data-id="element-1729" data-mp="fk7sf3400ah6">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1730" data-mp="fkr7mc29kkri">
              Department *
            </label>
            <select value={department} onChange={e => {
            setDepartment(e.target.value);
            setFormError(null);
          }} className={drawerInputCls} data-id="element-1731" data-mp="tks83qem9q6g">
              <option value="" data-id="element-1732" data-mp="12j3ybrbkmlyd">Select a department…</option>
              {departments.map(item => <option key={item} value={item} data-id="element-1733" data-mp="yjdb1xl96nyl">
                  {item}
                </option>)}
            </select>
          </div>
          <div data-id="element-1734" data-mp="1c9gs8a59zrh6">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1735" data-mp="1cc2v3wh81it2">
              Date
            </label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} className={drawerInputCls} data-id="element-1736" data-mp="1m85y14jwdlh2" />
          </div>
          {selectedUser && <div className="flex items-end pb-0.5 text-xs text-slate-500" data-id="element-1737" data-mp="1ha4iuuple6av">
              Time will be costed at <span className="ml-1 font-semibold text-slate-700" data-id="element-1738" data-mp="1odb1nkknyojr">£{selectedUser.hourlyCost.toFixed(2)}/hr</span>.
            </div>}
          <div className="sm:col-span-2" data-id="element-1739" data-mp="1t99srfxdmknv">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1740" data-mp="1y9xg9rmecjdc">
              Notes
            </label>
            <input type="text" value={notes} onChange={e => setNotes(e.target.value)} className={drawerInputCls} placeholder="Optional notes" data-id="element-1741" data-mp="2327erw1kurr8" />
          </div>
        </div>
        {formError && <p className="text-xs text-rose-600 flex items-center gap-1.5" data-id="element-1742" data-mp="2c12m5bgcy7w4">
            <AlertTriangle className="w-3.5 h-3.5" data-id="element-1743" data-mp="2h0xap8cnule0" />
            {formError}
          </p>}
        <button type="button" onClick={add} disabled={!canAdd} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#6A9A24] hover:bg-[#587F1D] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors" data-id="element-1744" data-mp="2gz46s0tfmf9p">
          <Plus className="w-4 h-4" data-id="element-1745" data-mp="rn22c0bn9euf" />
          Log time
        </button>
      </AddItemSection>
    </div>;
}
// --- Invoices Panel ---
function InvoicesPanel({
  invoices,
  departments,
  currentUser,
  isAdmin,
  onAdd,
  onRemove
}: {
  invoices: Invoice[];
  departments: string[];
  currentUser: string;
  isAdmin: boolean;
  onAdd: (i: Invoice) => void;
  onRemove: (id: string) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');
  const [category, setCategory] = useState<InvoiceCategory>('Interim');
  const [department, setDepartment] = useState('');
  const [paymentStatus, setPaymentStatus] = useState<InvoicePaymentStatus>('unpaid');
  const [sageId, setSageId] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<{
    name: string;
    url: string;
  } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const total = invoices.filter(invoice => invoice.paymentStatus !== 'cancelled').reduce((sum, invoice) => sum + invoice.value, 0);
  const numericValue = Number(value);
  const canAdd = !!value && numericValue !== 0 && !!department;
  const handleFile = (files: FileList | null) => {
    const selected = files?.[0];
    if (!selected) return;
    setFile({
      name: selected.name,
      url: URL.createObjectURL(selected)
    });
  };
  const add = () => {
    if (!value || numericValue === 0) {
      setFormError('Enter a non-zero value.');
      return;
    }
    if (!department) {
      setFormError('Select a department.');
      return;
    }
    onAdd({
      id: Math.random().toString(36).substr(2, 9),
      value: category === 'Credit note' ? -Math.abs(numericValue) : Math.abs(numericValue),
      category,
      department,
      paymentStatus: category === 'Credit note' ? 'paid' : paymentStatus,
      sageId: sageId.trim() || undefined,
      description: description.trim() || undefined,
      fileName: file?.name,
      fileUrl: file?.url,
      addedBy: currentUser,
      date: new Date().toISOString()
    });
    setValue('');
    setCategory('Interim');
    setDepartment('');
    setPaymentStatus('unpaid');
    setSageId('');
    setDescription('');
    setFile(null);
    setFormError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  return <div className="p-6 space-y-5" data-id="element-1746" data-mp="2ya9glbrhyg65">
      {invoices.length === 0 ? <div className="flex flex-col items-center justify-center text-center py-10 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50" data-id="element-1747" data-mp="2yb192dpsrxs7">
          <FileText className="w-10 h-10 text-slate-300 mb-3" data-id="element-1748" data-mp="25m3r6bpi02zz" />
          <p className="text-sm font-medium text-slate-900" data-id="element-1749" data-mp="25nojduocb9uo">
            No invoices raised
          </p>
          <p className="text-xs text-slate-500 mt-1" data-id="element-1750" data-mp="2kgi3xuigyg67">
            Raise separate invoice entries for each department.
          </p>
        </div> : <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100" data-id="element-1751" data-mp="3vx8chkad7k2z">
          {invoices.map(invoice => {
        const canDelete = isAdmin || invoice.addedBy === currentUser;
        const isCredit = invoice.value < 0;
        const isCancelled = invoice.paymentStatus === 'cancelled';
        const invoiceDetails = <>
                <span className="w-9 h-9 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-1752" data-mp="4jdc1gg5bjfz">
                  <FileText className="w-4 h-4" data-id="element-1753" data-mp="3rmi8sahlt33h" />
                </span>
                <div className="min-w-0" data-id="element-1754" data-mp="s6lgjw5r2ogy">
                  <div className="flex items-center gap-2 flex-wrap" data-id="element-1755" data-mp="s5a5db9z6eft">
                    <span className={`text-sm font-semibold tabular-nums ${isCredit ? 'text-rose-600' : 'text-slate-900'}`} data-id="element-1756" data-mp="n70a70o93du7">
                      {isCredit ? `−${formatCurrency(Math.abs(invoice.value))}` : formatCurrency(invoice.value)}
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px] font-medium" data-id="element-1757" data-mp="eddroc92072y">
                      <Tag className="w-3 h-3" data-id="element-1758" data-mp="165bh5lrs36a2" />
                      {invoice.category}
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[11px] font-medium" data-id="element-1759" data-mp="2oufmm13ii394">
                      {invoice.department}
                    </span>
                    {!isCredit && <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${isCancelled ? 'bg-slate-200 text-slate-600' : invoice.paymentStatus === 'paid' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`} data-id="element-1760" data-mp="2xvx2bid0c2xa">
                        {isCancelled ? 'Cancelled' : invoice.paymentStatus === 'paid' ? 'Paid' : 'Outstanding'}
                      </span>}
                    {invoice.fileUrl && <span className="inline-flex items-center gap-1 text-[11px] text-[#14466B] font-medium group-hover:underline" data-id="element-1761" data-mp="3ljdijmn0077v">
                        <ExternalLink className="w-3 h-3" data-id="element-1762" data-mp="3qjj54oeev3j3" />
                        Attachment
                      </span>}
                  </div>
                  {invoice.description && <p className="mt-1 text-xs text-slate-600" data-id="element-1763" data-mp="1vf1xkopqy5il">{invoice.description}</p>}
                  <p className="text-xs text-slate-500 mt-1" data-id="element-1764" data-mp="1vcz0jkq6fc4d">
                    {invoice.addedBy} ·{' '}
                    {new Date(invoice.date).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              })}{invoice.sageId ? ` · Sage ID: ${invoice.sageId}` : ''}
                  </p>
                </div>
              </>;
        return <div key={invoice.id} className="flex items-start justify-between gap-3 px-4 py-3 hover:bg-slate-50 transition-colors group" data-id="element-1765" data-mp="2a4hl7vtzduu2">
                {invoice.fileUrl ? <a href={invoice.fileUrl} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 min-w-0 flex-1" title="Open attachment in a new tab" data-id="element-1766" data-mp="2o37hxacw1shw">
                    {invoiceDetails}
                  </a> : <div className="flex items-start gap-3 min-w-0 flex-1" data-id="element-1767" data-mp="trsrssfytr1m">
                    {invoiceDetails}
                  </div>}
                {canDelete && <button type="button" onClick={() => onRemove(invoice.id)} className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0" aria-label="Delete invoice" data-id="element-1768" data-mp="30dyk34d9klgu">
                    <Trash2 className="w-4 h-4" data-id="element-1769" data-mp="2qnnab16wfi21" />
                  </button>}
              </div>;
      })}
          <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50" data-id="element-1770" data-mp="2vlx9dmvqv2sx">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-1771" data-mp="2hmpa6tzkzuoq">
              Active invoice total
            </span>
            <span className={`text-sm font-bold tabular-nums ${total < 0 ? 'text-rose-600' : 'text-slate-900'}`} data-id="element-1772" data-mp="2mkh05igo16qa">
              {total < 0 ? `−${formatCurrency(Math.abs(total))}` : formatCurrency(total)}
            </span>
          </div>
        </div>}

      <AddItemSection label="Add new invoice" data-id="element-1773" data-mp="5z900q7gt81i">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1774" data-mp="5wd6rf6kb7je">
          <div data-id="element-1775" data-mp="awr2pyuw2r12">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1776" data-mp="av6jh5e0yuii">
              Value *
            </label>
            <input type="number" min="0" step="0.01" value={value} onChange={e => {
            setValue(e.target.value);
            setFormError(null);
          }} className={drawerInputCls} placeholder="0.00" data-id="element-1777" data-mp="22yu2pjubpzjo" />
            {category === 'Credit note' && <p className="mt-1 text-xs text-rose-600" data-id="element-1778" data-mp="1szl42doijwqz">
                Saved as a negative value and deducted from the invoice total.
              </p>}
          </div>
          <div data-id="element-1779" data-mp="1jb323t4hzjlr">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1780" data-mp="1jakxo97olz2r">
              Category *
            </label>
            <select value={category} onChange={e => setCategory(e.target.value as InvoiceCategory)} className={drawerInputCls} data-id="element-1781" data-mp="1f9rc3mhpowd1">
              {INVOICE_CATEGORIES.map(item => <option key={item} value={item} data-id="element-1782" data-mp="15blc5afgu0at">
                  {item}
                </option>)}
            </select>
          </div>
          <div data-id="element-1783" data-mp="10da0cu5m1fpy">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1784" data-mp="10cqsy7dl69q6">
              Department *
            </label>
            <select value={department} onChange={e => {
            setDepartment(e.target.value);
            setFormError(null);
          }} className={drawerInputCls} data-id="element-1785" data-mp="v74wah1zkj70">
              <option value="" data-id="element-1786" data-mp="106zlmg6450py">Select a department…</option>
              {departments.map(item => <option key={item} value={item} data-id="element-1787" data-mp="154srt97f1uq8">
                  {item}
                </option>)}
            </select>
          </div>
          <div data-id="element-1788" data-mp="7fpqufewxlrj">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1789" data-mp="cgdq1odap8wu">
              Payment status *
            </label>
            <select value={category === 'Credit note' ? 'paid' : paymentStatus} onChange={e => setPaymentStatus(e.target.value as InvoicePaymentStatus)} disabled={category === 'Credit note'} className={`${drawerInputCls} disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed`} data-id="element-1790" data-mp="heozvz3lbjf2">
              <option value="unpaid" data-id="element-1791" data-mp="3pui7fofy1kjz">Outstanding</option>
              <option value="paid" data-id="element-1792" data-mp="3huuccn9h7fot">Paid</option>
              <option value="cancelled" data-id="element-1793" data-mp="3htj8kng167xp">Cancelled</option>
            </select>
          </div>
          <div data-id="element-1794" data-mp="3cupv2v0qugxj">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1795" data-mp="3cv7xyahwvtef">
              Sage ID <span className="text-slate-400" data-id="element-1796" data-mp="3rjc7anv2qskj">(optional)</span>
            </label>
            <input type="text" value={sageId} onChange={e => setSageId(e.target.value)} className={drawerInputCls} placeholder="e.g. SI-2026-001" data-id="element-1797" data-mp="3mlaps7dar1b6" />
          </div>
          <div className="sm:col-span-2" data-id="element-1798" data-mp="2yxt1wmx5kndh">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1799" data-mp="2txyewugi0hxp">
              Description <span className="text-slate-400" data-id="element-1800" data-mp="38ufn5cte5gg1">(optional)</span>
            </label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} className={`${drawerInputCls} resize-y`} placeholder="Add a short description for this invoice" data-id="element-1801" data-mp="33vckfz0vvmb8" />
          </div>
          <div data-id="element-1802" data-mp="j5n3nggmdqfw">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1803" data-mp="j3sl28stmaez">
              Attachment <span className="text-slate-400" data-id="element-1804" data-mp="qnbivl6q487m">(optional)</span>
            </label>
            <input ref={fileInputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={e => handleFile(e.target.files)} data-id="element-1805" data-mp="vnz9i5r1in1b" />
            <div className="flex items-center gap-2 min-h-[38px]" data-id="element-1806" data-mp="hphpai0cfso5">
              <button type="button" onClick={() => fileInputRef.current?.click()} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-medium text-slate-700 transition-colors" data-id="element-1807" data-mp="lqbc1821v1fg">
                <Upload className="w-3.5 h-3.5" data-id="element-1808" data-mp="1eg1q5h3om3bk" />
                {file ? 'Replace file' : 'Upload file'}
              </button>
              {file && <span className="inline-flex items-center gap-1.5 min-w-0 text-xs text-slate-600" data-id="element-1809" data-mp="14nm8ym07lirq">
                  <FileText className="w-3.5 h-3.5 flex-shrink-0 text-slate-400" data-id="element-1810" data-mp="1sd6u9bju4s46" />
                  <span className="truncate" data-id="element-1811" data-mp="1x46r6gsyfa6o">{file.name}</span>
                </span>}
            </div>
          </div>
        </div>
        {formError && <p className="text-xs text-rose-600 flex items-center gap-1.5" data-id="element-1812" data-mp="18ekkc2e1r0fs">
            <AlertTriangle className="w-3.5 h-3.5" data-id="element-1813" data-mp="13msszpn2gqol" />
            {formError}
          </p>}
        <button type="button" onClick={add} disabled={!canAdd} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#6A9A24] hover:bg-[#587F1D] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors" data-id="element-1814" data-mp="yng7b87xlpnl">
          <Plus className="w-4 h-4" data-id="element-1815" data-mp="1rbvjlmzohtv8" />
          Add invoice
        </button>
      </AddItemSection>
    </div>;
}
// --- Expenses Panel ---
function ExpensesPanel({
  expenses,
  departments,
  currentUser,
  isAdmin,
  onAdd,
  onRemove
}: {
  expenses: Expense[];
  departments: string[];
  currentUser: string;
  isAdmin: boolean;
  onAdd: (e: Expense) => void;
  onRemove: (id: string) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');
  const [category, setCategory] = useState<ExpenseCategory | ''>('');
  const [department, setDepartment] = useState('');
  const [description, setDescription] = useState('');
  const [receipt, setReceipt] = useState<{
    name: string;
    url: string;
  } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const total = expenses.reduce((sum, e) => sum + e.value, 0);
  const numericValue = Number(value);
  const canAdd = !!value && numericValue > 0 && !!category && !!department && !!receipt;
  const handleFile = (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    setReceipt({
      name: file.name,
      url: URL.createObjectURL(file)
    });
    setFormError(null);
  };
  const add = () => {
    if (!value || numericValue <= 0) {
      setFormError('Enter a value greater than 0.');
      return;
    }
    if (!category) {
      setFormError('Select a category.');
      return;
    }
    if (!department) {
      setFormError('Select a department.');
      return;
    }
    if (!receipt) {
      setFormError('Attach a receipt file or image.');
      return;
    }
    onAdd({
      id: Math.random().toString(36).substr(2, 9),
      value: numericValue,
      category,
      department,
      description: description.trim() || undefined,
      receiptName: receipt.name,
      receiptUrl: receipt.url,
      addedBy: currentUser,
      date: new Date().toISOString()
    });
    setValue('');
    setCategory('');
    setDepartment('');
    setDescription('');
    setReceipt(null);
    setFormError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  return <div className="p-6 space-y-5" data-id="element-1816" data-mp="62im9pudsygp">
      {/* Expense list */}
      {expenses.length === 0 ? <div className="flex flex-col items-center justify-center text-center py-10 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50" data-id="element-1817" data-mp="au0qqnvm5h6f">
          <Receipt className="w-10 h-10 text-slate-300 mb-3" data-id="element-1818" data-mp="3agql8618h7h8" />
          <p className="text-sm font-medium text-slate-900" data-id="element-1819" data-mp="3ehsp5063i7pj">
            No expenses raised
          </p>
          <p className="text-xs text-slate-500 mt-1" data-id="element-1820" data-mp="36y060di3eqxj">
            Add an expense below. Each expense needs a value, category and
            receipt.
          </p>
        </div> : <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100" data-id="element-1821" data-mp="2xy4rpzt8qfta">
          {expenses.map(exp => {
        const canDelete = isAdmin || exp.addedBy === currentUser;
        return <div key={exp.id} className="flex items-start justify-between gap-3 px-4 py-3 hover:bg-slate-50 transition-colors group" data-id="element-1822" data-mp="2o0qmkwcrpdga">
                <a href={exp.receiptUrl} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 min-w-0 flex-1" title="Open receipt in a new tab" data-id="element-1823" data-mp="2j7xhu8hau547">
                  <span className="w-9 h-9 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-1824" data-mp="2a9lhheqmrsyk">
                    <Receipt className="w-4 h-4" data-id="element-1825" data-mp="258xq2ryhpa3t" />
                  </span>
                  <div className="min-w-0" data-id="element-1826" data-mp="20bxkei5jy9d8">
                    <div className="flex items-center gap-2 flex-wrap" data-id="element-1827" data-mp="20cyykvhv7bz6">
                      <span className="text-sm font-semibold text-slate-900 tabular-nums" data-id="element-1828" data-mp="1qlpa7du8kgli">
                        {formatCurrency(exp.value)}
                      </span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px] font-medium" data-id="element-1829" data-mp="1gpw0013zoz6w">
                        <Tag className="w-3 h-3" data-id="element-1830" data-mp="1lpqra5rofqsh" />
                        {exp.category}
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[11px] font-medium" data-id="element-1831" data-mp="2ecbhh2wf1wug">
                        {exp.department}
                      </span>
                      <span className="inline-flex items-center gap-1 text-[11px] text-[#14466B] font-medium group-hover:underline" data-id="element-1832" data-mp="24m9s87sbu8dy">
                        <ExternalLink className="w-3 h-3" data-id="element-1833" data-mp="2sat2gsuxsv8o" />
                        Receipt
                      </span>
                    </div>
                    {exp.description && <p className="text-sm text-slate-700 mt-1 truncate" data-id="element-1834" data-mp="lnk8ddys2dyr">
                        {exp.description}
                      </p>}
                    <p className="text-xs text-slate-500 mt-0.5" data-id="element-1835" data-mp="gohdungfbq51">
                      {exp.addedBy} ·{' '}
                      {new Date(exp.date).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })}
                    </p>
                  </div>
                </a>
                {canDelete && <button type="button" onClick={() => onRemove(exp.id)} className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0" aria-label="Delete expense" data-id="element-1836" data-mp="3twu9i3p3c4t0">
                    <Trash2 className="w-4 h-4" data-id="element-1837" data-mp="7mrgl0zv1wzc" />
                  </button>}
              </div>;
      })}
          <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50" data-id="element-1838" data-mp="1n39oqdz69wpc">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-1839" data-mp="1j2pvhlhpzd8h">
              Total
            </span>
            <span className="text-sm font-bold text-slate-900 tabular-nums" data-id="element-1840" data-mp="2eg0ot7vbn5b2">
              {formatCurrency(total)}
            </span>
          </div>
        </div>}

      {/* Add expense */}
      <AddItemSection label="Add new expense" data-id="element-1841" data-mp="2ngfcgdvrcwhq">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1842" data-mp="2sdys9i83thbs">
          <div data-id="element-1843" data-mp="2sb2t5qkismlr">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1844" data-mp="2xcs3f3zx4w8s">
              Value *
            </label>
            <input type="number" min="0" step="0.01" value={value} onChange={e => {
            setValue(e.target.value);
            setFormError(null);
          }} className={drawerInputCls} placeholder="0.00" data-id="element-1845" data-mp="31dbze48r9ttc" />
          </div>
          <div data-id="element-1846" data-mp="3b3f4v3x2d08k">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1847" data-mp="3b619auonqzmk">
              Category *
            </label>
            <select value={category} onChange={e => {
            setCategory(e.target.value as ExpenseCategory);
            setFormError(null);
          }} className={drawerInputCls} data-id="element-1848" data-mp="3l24d04z5r6bm">
              <option value="" data-id="element-1849" data-mp="19cenywvjk236">Select a category…</option>
              {EXPENSE_CATEGORIES.map(c => <option key={c} value={c} data-id="element-1850" data-mp="14hr8fniimi0q">
                  {c}
                </option>)}
            </select>
          </div>
          <div data-id="element-1851" data-mp="uhqjnm1vn9p5">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1852" data-mp="ui0ebmxva516">
              Department *
            </label>
            <select value={department} onChange={e => {
            setDepartment(e.target.value);
            setFormError(null);
          }} className={drawerInputCls} data-id="element-1853" data-mp="1s3xp0y60962k">
              <option value="" data-id="element-1854" data-mp="1iexq1eri6aod">Select a department…</option>
              {departments.map(item => <option key={item} value={item} data-id="element-1855" data-mp="1dgmbiqq9jbzt">
                  {item}
                </option>)}
            </select>
          </div>
          <div className="sm:col-span-2" data-id="element-1856" data-mp="2aws1nlg5om3m">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1857" data-mp="2axl4ibbzurgh">
              Receipt *
            </label>
            <input ref={fileInputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={e => handleFile(e.target.files)} data-id="element-1858" data-mp="47q758zz8gu5" />
            <div className="flex items-center gap-2" data-id="element-1859" data-mp="961g7hig6nbb">
              <button type="button" onClick={() => fileInputRef.current?.click()} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-medium text-slate-700 transition-colors" data-id="element-1860" data-mp="3qnkm6wn3onni">
                <Upload className="w-3.5 h-3.5" data-id="element-1861" data-mp="3hkt9ktihnlua" />
                {receipt ? 'Replace file' : 'Upload file / image'}
              </button>
              {receipt && <span className="inline-flex items-center gap-1.5 min-w-0 text-xs text-slate-600" data-id="element-1862" data-mp="37ogi82af1k2p">
                  <FileText className="w-3.5 h-3.5 flex-shrink-0 text-slate-400" data-id="element-1863" data-mp="3cn1k065bgbt9" />
                  <span className="truncate" data-id="element-1864" data-mp="15wtwaf5dsfd3">{receipt.name}</span>
                </span>}
            </div>
          </div>
          <div className="sm:col-span-2" data-id="element-1865" data-mp="1awgaaqt19zmy">
            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1866" data-mp="1avenjp2nnope">
              Description
            </label>
            <input type="text" value={description} onChange={e => setDescription(e.target.value)} className={drawerInputCls} placeholder="Optional description" data-id="element-1867" data-mp="2vbmfiub9hfks" />
          </div>
        </div>
        {formError && <p className="text-xs text-rose-600 flex items-center gap-1.5" data-id="element-1868" data-mp="355li1uf8z1d9">
            <AlertTriangle className="w-3.5 h-3.5" data-id="element-1869" data-mp="30bqxkortiid4" />
            {formError}
          </p>}
        <button type="button" onClick={add} disabled={!canAdd} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#6A9A24] hover:bg-[#587F1D] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors" data-id="element-1870" data-mp="3e2vpf4oy26cw">
          <Plus className="w-4 h-4" data-id="element-1871" data-mp="3j3t8djhs34fg" />
          Add expense
        </button>
      </AddItemSection>
    </div>;
}
// --- Job Notes & Files Panel ---
function JobNotesPanel({
  notes,
  attachments,
  newNote,
  setNewNote,
  onAddNote,
  onAddAttachment,
  onRemoveAttachment
}: {
  notes: JobNote[];
  attachments: Attachment[];
  newNote: string;
  setNewNote: (v: string) => void;
  onAddNote: () => void;
  onAddAttachment: (a: Attachment) => void;
  onRemoveAttachment: (id: string) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleFiles = (files: FileList | null) => {
    if (!files) return;
    Array.from(files).forEach(f => {
      onAddAttachment({
        id: Math.random().toString(36).substr(2, 9),
        name: f.name,
        size: f.size > 1024 * 1024 ? `${(f.size / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(f.size / 1024))} KB`,
        uploadedAt: new Date().toISOString()
      });
    });
  };
  return <div className="p-6 space-y-6" data-id="element-1872" data-mp="txrhr8owf9b1">
      {/* Files */}
      <div data-id="element-1873" data-mp="xybb016cpsrw">
        <div className="flex items-center justify-between mb-2" data-id="element-1874" data-mp="xy1k8bdhfhl5">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5" data-id="element-1875" data-mp="25bd4kn7acyte">
            <Paperclip className="w-4 h-4" data-id="element-1876" data-mp="29bdscyg9ymd4" />
            Attachments
          </h3>
          <button type="button" onClick={() => fileInputRef.current?.click()} className="flex items-center gap-1.5 text-sm font-medium text-[#14466B] hover:underline" data-id="element-1877" data-mp="3tuzckj3tz628">
            <Upload className="w-3.5 h-3.5" data-id="element-1878" data-mp="3pv8hi379u3rw" />
            Upload
          </button>
          <input ref={fileInputRef} type="file" multiple className="hidden" onChange={e => handleFiles(e.target.files)} data-id="element-1879" data-mp="3l0b7asy99odf" />
        </div>
        {attachments.length === 0 ? <p className="text-sm text-slate-400 py-2" data-id="element-1880" data-mp="3b516g8jlfn18">No files attached yet.</p> : <div className="space-y-2" data-id="element-1881" data-mp="36550w4v8znjk">
            {attachments.map(a => <div key={a.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors group" data-id="element-1882" data-mp="363k8akshueoc">
                <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" data-id="element-1883" data-mp="2x4yuf2xyvntw" />
                <div className="min-w-0 flex-1" data-id="element-1884" data-mp="10bn9d1191jad">
                  <p className="text-sm text-slate-800 truncate" data-id="element-1885" data-mp="10bew3817sbyn">{a.name}</p>
                  <p className="text-xs text-slate-400" data-id="element-1886" data-mp="vdvgo4ss5t54">{a.size}</p>
                </div>
                <button type="button" onClick={() => onRemoveAttachment(a.id)} className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" aria-label="Remove attachment" data-id="element-1887" data-mp="1lly37gli14he">
                  <X className="w-4 h-4" data-id="element-1888" data-mp="1udzzd2ojbylv" />
                </button>
              </div>)}
          </div>}
      </div>

      {/* Notes */}
      <div data-id="element-1889" data-mp="1qe9cha24q2mm">
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5" data-id="element-1890" data-mp="1qfkf36ju94c5">
          <StickyNote className="w-4 h-4" data-id="element-1891" data-mp="xosgk2faw3ln" />
          Notes
        </h3>
        <div className="space-y-2" data-id="element-1892" data-mp="xqvdz7is8z0e">
          <textarea value={newNote} onChange={e => setNewNote(e.target.value)} rows={2} className={`${drawerInputCls} resize-none`} placeholder="Add a project note..." data-id="element-1893" data-mp="1bp352yt5163n" />
          <button type="button" onClick={onAddNote} disabled={!newNote.trim()} className="px-4 py-2 rounded-lg bg-[#6A9A24] hover:bg-[#587F1D] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors" data-id="element-1894" data-mp="16rtj5t4yjckk">
            Add note
          </button>
        </div>
        <div className="mt-4 space-y-3" data-id="element-1895" data-mp="2qnqykfmejtiv">
          {notes.length === 0 ? <p className="text-sm text-slate-400" data-id="element-1896" data-mp="2uo12znujqbvm">No notes yet.</p> : notes.map(note => <article key={note.id} className="border border-slate-200 rounded-xl p-4 bg-white" data-id="element-1897" data-mp="2go04ug5o1iju">
                <header className="flex items-center justify-between mb-2" data-id="element-1898" data-mp="2lm1n4yv9pdu9">
                  <div className="flex items-center gap-2 min-w-0" data-id="element-1899" data-mp="39c5kwplryjdc">
                    <div className="w-7 h-7 rounded-full bg-[#14466B] text-white text-[11px] font-semibold flex items-center justify-center flex-shrink-0" data-id="element-1900" data-mp="39cxho3r3yn57">
                      {note.author.split(' ').map(s => s[0]).slice(0, 2).join('')}
                    </div>
                    <span className="text-sm font-medium text-slate-900 truncate" data-id="element-1901" data-mp="34kdv4hag0ho9">
                      {note.author}
                    </span>
                  </div>
                  <time className="text-xs text-slate-400 tabular-nums flex-shrink-0" data-id="element-1902" data-mp="w2fkqi4x6s6">
                    {new Date(note.createdAt).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              })}
                  </time>
                </header>
                <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-id="element-1903" data-mp="tdbk0vhrwr3">
                  {note.body}
                </p>
              </article>)}
        </div>
      </div>
    </div>;
}
function getPersonInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}
function PersonAvatar({
  name,
  roleLabel,
  tone
}: {
  name: string;
  roleLabel: string;
  tone: 'director' | 'manager';
}) {
  const {
    users
  } = useSettingsStore();
  const match = users.find(user => `${user.firstName} ${user.lastName}`.toLowerCase() === name.trim().toLowerCase());
  const label = `${roleLabel}: ${name || 'Unassigned'}`;
  const toneCls = tone === 'director' ? 'bg-[#14466B] text-white' : 'bg-slate-100 text-[#14466B]';
  if (!name.trim()) {
    return <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-slate-100 text-[11px] font-semibold text-slate-300" title={`${roleLabel}: Unassigned`} data-id="element-1904" data-mp="1ks59g9ce3og6">
        —<span className="sr-only" data-id="element-1905" data-mp="1tommji4bdwek">{`${roleLabel} unassigned`}</span>
      </span>;
  }
  return <span className={`inline-flex h-8 w-8 items-center justify-center overflow-hidden rounded-full border-2 border-white text-[11px] font-semibold ${toneCls}`} title={label} data-id="element-1906" data-mp="wyn8j85ea5ol">
      {match?.profileUrl ? <img src={match.profileUrl} alt="" className="h-full w-full object-cover" data-id="element-1907" data-mp="15zbmk2usg61h" /> : getPersonInitials(name)}
      <span className="sr-only" data-id="element-1908" data-mp="1atzds9h6fejr">{label}</span>
    </span>;
}
// --- Department Budget Allocations Tab ---
type BudgetAllocationsTabProps = {
  jobs: Job[];
  onOpenJob: (job: Job) => void;
  onUpdateBudget: (jobId: string, department: string, monthKey: string, amount: number) => void;
  onValidateBudget: (jobId: string, department: string, monthKey: string) => void;
  onUnvalidateBudget: (jobId: string, department: string, monthKey: string) => void;
  showValidatedOnly: boolean;
};
function BudgetAllocationsTab({
  jobs,
  onOpenJob,
  onUpdateBudget,
  onValidateBudget,
  onUnvalidateBudget,
  showValidatedOnly
}: BudgetAllocationsTabProps) {
  const [monthOffset, setMonthOffset] = useState(0);
  const [expandedJobs, setExpandedJobs] = useState<Set<string>>(() => new Set());
  const months = useMemo(() => {
    const now = new Date();
    return [-1, 0, 1].map(offset => {
      const date = new Date(now.getFullYear(), now.getMonth() + monthOffset + offset, 1);
      return {
        key: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
        label: date.toLocaleDateString('en-GB', {
          month: 'short',
          year: 'numeric'
        })
      };
    });
  }, [monthOffset]);
  const getDepartmentTotal = (job: Job, department: string) => Object.values(job.departmentBudgets?.[department] || {}).reduce((sum, amount) => sum + amount, 0);
  const getJobTotal = (job: Job) => job.departments.reduce((sum, department) => sum + getDepartmentTotal(job, department), 0);
  const isDepartmentMonthValidated = (job: Job, department: string, monthKey: string) => job.validatedDepartmentBudgets?.[department]?.[monthKey] === true;
  const isJobMonthValidated = (job: Job, monthKey: string) => {
    const departmentsWithAllocations = job.departments.filter(department => (job.departmentBudgets?.[department]?.[monthKey] || 0) > 0);
    return departmentsWithAllocations.length > 0 && departmentsWithAllocations.every(department => isDepartmentMonthValidated(job, department, monthKey));
  };
  const hasValidatedAllocation = (job: Job) => job.departments.some(department => months.some(month => isDepartmentMonthValidated(job, department, month.key)));
  const visibleJobs = showValidatedOnly ? jobs.filter(hasValidatedAllocation) : jobs;
  const getJobMonthTotal = (job: Job, monthKey: string) => job.departments.reduce((sum, department) => {
    const amount = job.departmentBudgets?.[department]?.[monthKey] || 0;
    return sum + (showValidatedOnly && !isDepartmentMonthValidated(job, department, monthKey) ? 0 : amount);
  }, 0);
  const getMonthTotal = (monthKey: string) => visibleJobs.reduce((sum, job) => sum + getJobMonthTotal(job, monthKey), 0);
  const monthlyTotals = months.map(month => getMonthTotal(month.key));
  const visibleTotal = monthlyTotals.reduce((sum, amount) => sum + amount, 0);
  const toggleJob = (jobId: string) => {
    setExpandedJobs(current => {
      const next = new Set(current);
      next.has(jobId) ? next.delete(jobId) : next.add(jobId);
      return next;
    });
  };
  const exportAllocations = () => {
    type AllocationRow = {
      job: Job;
      department: string;
      allocated: number;
    };
    const rows: AllocationRow[] = visibleJobs.flatMap(job => job.departments.filter(department => !showValidatedOnly || months.some(month => isDepartmentMonthValidated(job, department, month.key))).map(department => ({
      job,
      department,
      allocated: showValidatedOnly ? months.reduce((sum, month) => sum + (isDepartmentMonthValidated(job, department, month.key) ? job.departmentBudgets?.[department]?.[month.key] || 0 : 0), 0) : getDepartmentTotal(job, department)
    })));
    downloadCsv<AllocationRow>(`monthly-budget-allocations-${months[0].key}.csv`, [{
      label: 'Job ID',
      value: row => row.job.ref
    }, {
      label: 'Job title',
      value: row => row.job.name
    }, {
      label: 'Client',
      value: row => row.job.client
    }, {
      label: 'Status',
      value: row => STATUS_CONFIG[row.job.status].label
    }, {
      label: 'Project director',
      value: row => row.job.projectDirector
    }, {
      label: 'Project manager',
      value: row => row.job.projectManager
    }, {
      label: 'Department',
      value: row => row.department
    }, {
      label: 'Job value',
      value: row => row.job.value
    }, {
      label: 'Allocated',
      value: row => row.allocated
    }, ...months.flatMap(month => [{
      label: month.label,
      value: (row: AllocationRow) => showValidatedOnly && !isDepartmentMonthValidated(row.job, row.department, month.key) ? 0 : row.job.departmentBudgets?.[row.department]?.[month.key] || 0
    }, {
      label: `${month.label} validated`,
      value: (row: AllocationRow) => isDepartmentMonthValidated(row.job, row.department, month.key) ? 'Yes' : 'No'
    }])], rows);
  };
  return <div className="flex-1 flex flex-col min-h-0 p-5 md:p-8 gap-4" data-id="element-1909" data-mp="109ptzy77xik5">
      <div className="flex items-center justify-between gap-3 flex-wrap" data-id="element-1910" data-mp="vh51d001tbi7">
        <div data-id="element-1911" data-mp="7ru6fz5dqbbz">
          <h2 className="text-base font-semibold text-slate-900" data-id="element-1912" data-mp="7raowjjn5oyh">
            Monthly budget allocations
          </h2>
          <p className="text-xs text-slate-500 mt-0.5" data-id="element-1913" data-mp="2rpt2hzrpwpz">
            Allocate budget at department level; project totals roll up
            automatically.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-id="element-1914" data-mp="2j7cjub85euf7">
          <div className="inline-flex items-center rounded-lg border border-slate-200 bg-white p-1" aria-label="Budget month navigation" data-id="element-1915" data-mp="2j8nqciszaga0">
            <button type="button" onClick={() => setMonthOffset(value => value - 1)} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100" aria-label="Show earlier months" data-id="element-1916" data-mp="29g88dlholrp4">
              <ChevronLeft className="w-4 h-4" data-id="element-1917" data-mp="325f46cpr436o" />
            </button>
            <button type="button" onClick={() => setMonthOffset(0)} className="px-2 text-xs font-medium text-[#14466B] hover:underline" data-id="element-1918" data-mp="323ku5tg0pvhd">
              Current month
            </button>
            <button type="button" onClick={() => setMonthOffset(value => value + 1)} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100" aria-label="Show later months" data-id="element-1919" data-mp="2t35wdulvanwz">
              <ChevronRight className="w-4 h-4" data-id="element-1920" data-mp="1hqllsnw8fqxx" />
            </button>
          </div>
          <button type="button" onClick={exportAllocations} disabled={visibleJobs.length === 0} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:border-slate-100 disabled:text-slate-400" title="Export the allocations shown as CSV" data-id="element-1921" data-mp="1ryy0n3ejtgly">
            <Download className="h-4 w-4" data-id="element-1922" data-mp="196n5jvq4ymye" />
            Export CSV
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto border border-slate-200 rounded-xl bg-white shadow-sm" data-id="element-1923" data-mp="1e19ja5jowrln">
        <table className="w-full min-w-[880px] text-left border-collapse" data-id="element-1924" data-mp="149mz3jpdji6r">
          <thead className="sticky top-0 z-10 bg-slate-50" data-id="element-1925" data-mp="149wq9am5nve1">
            <tr className="border-b border-slate-200" data-id="element-1926" data-mp="q9m1cd4eoqvi">
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 w-[230px] min-w-[230px]" data-id="element-1927" data-mp="qc87q9fk90c5">
                Project / department
              </th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 min-w-[130px]" data-id="element-1928" data-mp="bp9gaegpe5xf">
                Status
              </th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 min-w-[80px]" data-id="element-1929" data-mp="kowj94p2zjsl">
                PD
              </th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 min-w-[80px]" data-id="element-1930" data-mp="fr3coajv8nrs">
                PM
              </th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right" data-id="element-1931" data-mp="pm53datshxwh">
                Job value
              </th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right" data-id="element-1932" data-mp="zczqehev11iz">
                Allocated
              </th>
              {months.map(month => <th key={month.key} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right min-w-[130px]" data-id="element-1933" data-mp="1da66u5c42o51">
                  {month.label}
                </th>)}
            </tr>
            <tr className="bg-slate-100/70 border-b border-slate-200" data-id="element-1934" data-mp="a1lb6col2kj6">
              <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-600" data-id="element-1935" data-mp="9ziljtrcetez">
                Monthly total
              </th>
              <th className="px-4 py-2.5" aria-label="Status total" data-id="element-1936" data-mp="jroasvq7mwqh" />
              <th className="px-4 py-2.5" aria-label="Project director total" data-id="element-1937" data-mp="oqhq8tp24xta" />
              <th className="px-4 py-2.5" aria-label="Project manager total" data-id="element-1938" data-mp="osls763ebmqx" />
              <th className="px-4 py-2.5 text-xs font-semibold text-slate-600 text-right tabular-nums" data-id="element-1939" data-mp="to14htlr9al8">
                {formatCurrency(visibleJobs.reduce((sum, job) => sum + job.value, 0))}
              </th>
              <th className="px-4 py-2.5 text-xs font-bold text-[#14466B] text-right tabular-nums" data-id="element-1940" data-mp="12phcjup3l0ma">
                {formatCurrency(visibleTotal)}
              </th>
              {monthlyTotals.map((total, index) => <th key={months[index].key} className="px-4 py-2.5 text-xs font-semibold text-slate-700 text-right tabular-nums" data-id="element-1941" data-mp="34jq5ngd42itl" data-mpi={index}>
                  {total ? formatCurrency(total) : <span className="font-normal text-slate-300" data-id="element-1942" data-mp="2s6cxhr5htiej">—</span>}
                </th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-1943" data-mp="2n8kxe9up2pz9">
            {visibleJobs.map(job => {
            const expanded = expandedJobs.has(job.id);
            const total = showValidatedOnly ? months.reduce((sum, month) => sum + getJobMonthTotal(job, month.key), 0) : getJobTotal(job);
            const overAllocated = job.value > 0 && total > job.value;
            return <Fragment key={job.id} data-id="element-1944" data-mp="35xriu4mvhvor">
                  <tr className="bg-white hover:bg-slate-50" data-id="element-1945" data-mp="360vvlqeelzr1">
                    <td className="px-4 py-3" data-id="element-1946" data-mp="31w60i3rkkp76">
                      <div className="flex items-center gap-2" data-id="element-1947" data-mp="31zjzr34xsiae">
                        <button type="button" onClick={() => toggleJob(job.id)} aria-expanded={expanded} aria-label={`${expanded ? 'Collapse' : 'Expand'} ${job.name} allocations`} className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700" data-id="element-1948" data-mp="1ul772uli4so4">
                          <ChevronRight className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`} data-id="element-1949" data-mp="3j6ib1qwajmp6" />
                        </button>
                        <button type="button" onClick={() => onOpenJob(job)} className="min-w-0 text-left group" data-id="element-1950" data-mp="34epvbdsl46mx">
                          <span className="block text-sm font-medium text-slate-900 transition-colors group-hover:text-[#14466B] group-hover:underline group-focus-visible:text-[#14466B] group-focus-visible:underline" data-id="element-1951" data-mp="zu55ytf2vig">
                            {job.name}
                          </span>
                          <span className="block text-xs text-slate-500 mt-0.5" data-id="element-1952" data-mp="3o6w688csn25s">
                            {job.ref} · {job.departments.length} department
                            {job.departments.length === 1 ? '' : 's'}
                          </span>
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-1953" data-mp="296fk4g5r3v1k">
                      <StatusBadge status={job.status} data-id="element-1954" data-mp="299a4y8sofhvi" />
                    </td>
                    <td className="px-4 py-3" data-id="element-1955" data-mp="2484bg8r6zqkx">
                      <PersonAvatar name={job.projectDirector} roleLabel="Project director" tone="director" data-id="element-1956" data-mp="2j66fsl61sg38" />
                    </td>
                    <td className="px-4 py-3" data-id="element-1957" data-mp="2e7v5k9buc3kh">
                      <PersonAvatar name={job.projectManager} roleLabel="Project manager" tone="manager" data-id="element-1958" data-mp="1qgz7iugskc4s" />
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-slate-900 text-right tabular-nums" data-id="element-1959" data-mp="1linyglybm5nm">
                      {formatCurrency(job.value)}
                    </td>
                    <td className={`px-4 py-3 text-sm font-semibold text-right tabular-nums ${overAllocated ? 'text-rose-600' : 'text-slate-900'}`} data-id="element-1960" data-mp="1vfiu8x37lry7">
                      {total ? formatCurrency(total) : <span className="font-normal text-slate-300" data-id="element-1961" data-mp="12rel21lfiwqr">—</span>}
                    </td>
                    {months.map(month => {
                  const monthTotal = getJobMonthTotal(job, month.key);
                  const isValidated = isJobMonthValidated(job, month.key);
                  return <td key={month.key} className={`px-4 py-3 text-sm text-right tabular-nums ${isValidated ? 'bg-emerald-50 font-semibold text-emerald-700' : 'text-slate-600'}`} data-id="element-1962" data-mp="217ud4n1xquoy">
                          {monthTotal ? formatCurrency(monthTotal) : <span className="text-slate-300" data-id="element-1963" data-mp="19h6h1pf4cfi5">—</span>}
                        </td>;
                })}
                  </tr>
                  {expanded && job.departments.filter(department => !showValidatedOnly || months.some(month => isDepartmentMonthValidated(job, department, month.key))).map(department => <tr key={`${job.id}-${department}`} className="bg-slate-50/70" data-id="element-1964" data-mp="1ngpkw60wd4on">
                        <td className="px-4 py-2.5 pl-11 text-sm text-slate-700" data-id="element-1965" data-mp="2tx1avjtkfg95">
                          <span className="font-medium underline decoration-slate-300 underline-offset-4" data-id="element-1966" data-mp="2yvm9xfx2ztw0">
                            {department}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-center text-xs text-slate-400" data-id="element-1967" data-mp="i9g09nlh4tf8">
                          —
                        </td>
                        <td className="px-4 py-2.5 text-center text-xs text-slate-400" data-id="element-1968" data-mp="dgv7mpeb0mda">
                          —
                        </td>
                        <td className="px-4 py-2.5 text-center text-xs text-slate-400" data-id="element-1969" data-mp="4hrojlf506ys">
                          —
                        </td>
                        <td className="px-4 py-2.5 text-right text-xs text-slate-400" data-id="element-1970" data-mp="3vn9ksm4r0sam">
                          —
                        </td>
                        <td className="px-4 py-2.5 text-sm font-medium text-slate-700 text-right tabular-nums" data-id="element-1971" data-mp="3qqstxk65zztu">
                          {showValidatedOnly ? (() => {
                    const total = months.reduce((sum, month) => sum + (isDepartmentMonthValidated(job, department, month.key) ? job.departmentBudgets?.[department]?.[month.key] || 0 : 0), 0);
                    return total ? formatCurrency(total) : <span className="text-slate-300" data-id="element-1972" data-mp="suh2e800qqn6">—</span>;
                  })() : getDepartmentTotal(job, department) ? formatCurrency(getDepartmentTotal(job, department)) : <span className="text-slate-300" data-id="element-1973" data-mp="xu3ege4420ue">—</span>}
                        </td>
                        {months.map(month => <td key={month.key} className="p-0" data-id="element-1974" data-mp="o27ib8ll4kst">
                            {!showValidatedOnly || isDepartmentMonthValidated(job, department, month.key) ? <BudgetCell value={job.departmentBudgets?.[department]?.[month.key] || 0} isValidated={Boolean(job.validatedDepartmentBudgets?.[department]?.[month.key])} onChange={amount => onUpdateBudget(job.id, department, month.key, amount)} onValidate={() => onValidateBudget(job.id, department, month.key)} onUnvalidate={() => onUnvalidateBudget(job.id, department, month.key)} data-id="element-1975" data-mp="a13dokmwfgg0" /> : <span className="flex min-h-[48px] items-center justify-end px-4 text-sm text-slate-300" data-id="element-1976" data-mp="1vfrb904hal6g">—</span>}
                          </td>)}
                      </tr>)}
                </Fragment>;
          })}
          </tbody>
        </table>
      </div>
    </div>;
}
function BudgetCell({
  value,
  isValidated,
  onChange,
  onValidate,
  onUnvalidate
}: {
  value: number;
  isValidated: boolean;
  onChange: (val: number) => void;
  onValidate: () => void;
  onUnvalidate: () => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value.toString());
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!isEditing) setTempValue(value ? value.toString() : '');
  }, [value, isEditing]);
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);
  const commit = (validate = false) => {
    const amount = Number(tempValue);
    if (tempValue.trim() === '' || Number.isNaN(amount) || amount < 0) {
      setTempValue(value ? value.toString() : '');
      setIsEditing(false);
      return;
    }
    if (amount !== value) onChange(amount);
    if (validate) onValidate();
    setIsEditing(false);
  };
  if (isEditing) {
    return <div className="flex min-h-[48px] bg-blue-50 focus-within:ring-2 focus-within:ring-inset focus-within:ring-blue-400" data-id="element-1977" data-mp="1jpd981gsakhx">
        <input ref={inputRef} type="number" min="0" step="0.01" value={tempValue} onChange={e => setTempValue(e.target.value)} onBlur={() => commit()} onKeyDown={e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        }
        if (e.key === 'Escape') {
          setTempValue(value ? value.toString() : '');
          setIsEditing(false);
        }
      }} className="min-w-0 flex-1 bg-transparent px-2 py-3 text-right text-sm text-slate-900 outline-none" placeholder="0" aria-label="Edit monthly sales allocation" data-id="element-1978" data-mp="1evr4qwkrxmx7" />
        <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => commit(true)} className="m-1 inline-flex w-8 flex-shrink-0 items-center justify-center rounded-md bg-emerald-600 text-white transition-colors hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-200" aria-label="Validate monthly sales allocation" title="Validate allocation" data-id="element-1979" data-mp="r0y4q8va3kku"><CheckCircle2 className="h-4 w-4" data-id="element-1980" data-mp="2xvrpnigdwazt" /></button>
      </div>;
  }
  return <div className={`flex min-h-[48px] w-full items-center justify-end gap-1 px-1 text-right text-sm transition-colors ${isValidated ? 'bg-emerald-50 font-semibold text-emerald-700' : 'text-slate-600 hover:bg-slate-100'}`} data-id="element-1981" data-mp="2xtx5w7gumoxb">
      <button type="button" onClick={() => setIsEditing(true)} className="flex min-h-[48px] min-w-0 flex-1 items-center justify-end gap-1.5 px-3 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-400" aria-label={`${isValidated ? 'Edit validated' : 'Edit'} monthly sales allocation`} title={isValidated ? 'Validated allocation — click to edit' : 'Click to edit'} data-id="element-1982" data-mp="29yuf3tyhibe7">
        {isValidated && <CheckCircle2 className="h-4 w-4 flex-shrink-0" aria-hidden="true" data-id="element-1983" data-mp="3vbxfsd6a1l3m" />}
        {value > 0 ? formatCurrency(value) : <span className="text-slate-300" data-id="element-1984" data-mp="44kze0d7xbor">—</span>}
      </button>
      {isValidated && <button type="button" onClick={onUnvalidate} className="mr-1 inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-emerald-700 transition-colors hover:bg-emerald-100 hover:text-rose-600 focus:outline-none focus:ring-2 focus:ring-rose-100" aria-label="Unvalidate monthly sales allocation" title="Unvalidate allocation" data-id="element-1985" data-mp="2088y82scqxyk"><X className="h-4 w-4" data-id="element-1986" data-mp="258wvv7b36d1r" /></button>}
    </div>;
}